import React, { useCallback, useState, useEffect, useRef } from 'react';
import { DoorOpen, GraduationCap, History, Swords, Volume2, VolumeX } from 'lucide-react';
import CardActionsMenu from './CardActionsMenu';
import LearningSessionHeader, { LearningImportButton } from './LearningSessionHeader';
import ReviewLevelUpDialog from './ReviewLevelUpDialog';
import './FlashCardMap.css';
import FlashCard from './FlashCard';
import ReadingArticlePractice from './ReadingArticlePractice';
import useReadingAudio from '../hooks/useReadingAudio';
import useAnswerFeedbackAudio from '../hooks/useAnswerFeedbackAudio';
import useCardFlipAudio from '../hooks/useCardFlipAudio';
import ReadingAudioControls from './ReadingAudioControls';
import EvaluationPanel from './EvaluationPanel';
import FlashCardForm from './FlashCardForm';
import LevelUpFlashCardForm from './LevelUpFlashCardForm';
import ScenePlayer from './ScenePlayer';
import NpcEncounterOverlay from './NpcEncounterOverlay';
import LearningCoach from './LearningCoach';
import useLearningCoaching from '../hooks/useLearningCoaching';
import { createCoachingSelector, getPracticeCardCoachingTrigger } from '../services/LearningCoaching';
import Spinner from './Spinner';
import ErrorMessage from './ErrorMessage';
import CelebrationPopup from './CelebrationPopup';
import TrainingAchievementOverlay from './TrainingAchievementOverlay';
import CardListeningControl from './CardListeningControl';
import { useConfirmDialog } from './ConfirmDialog';
import { t } from '../i18n';
import apiClient from '../services/apiClient';
import authService from '../services/AuthService';
import AutonomousSchedulerService from '../services/AutonomousSchedulerService';
import CardMessageService from '../services/CardMessageService';
import PracticeSettingsService from '../services/PracticeSettingsService';
import { ENCOUNTER_JUDGMENT_POINTS } from '../services/encounterRewards';
import { getCardLevelUpAnimation } from '../services/LevelUpCelebrationAssets';
import {
  createPracticeSessionRecordId,
  getPendingPracticeCheckpoint,
} from '../services/PracticeSessionPersistence';
import { getCurrentTrainerId, getEarlyGradeThreeTrainerImage } from '../services/TrainerAssets';
import {
  isPracticeSoundEnabled,
  playPracticeSound,
  setPracticeSoundEnabled,
  unlockPracticeAudio,
} from '../services/soundEffects';
import { isTtsAutoplayEnabled, setTtsAutoplayEnabled } from '../services/TtsPreferenceService';
import { stripSimpleMarkdown } from '../utils/simpleMarkdown';
import completionCelebrationSprite from '../assets/sprites/celerbration_complete.webp';
import timeCompletionCelebrationSprite from '../assets/sprites/celerbration_complete_time.webp';
import clockRingingSprite from '../assets/sprites/clock-ringing-sprite-sheet.webp';
import nextRepeat1Sprite from '../assets/sprites/nextRepeat1.webp';
import nextRepeat2Sprite from '../assets/sprites/nextRepeat2.webp';
import nextRepeat3Sprite from '../assets/sprites/nextRepeat3.webp';
import nextRepeat4Sprite from '../assets/sprites/nextRepeat4.webp';
import nextRepeat5Sprite from '../assets/sprites/nextRepeat5.webp';
import nextRepeat6Sprite from '../assets/sprites/nextRepeat6.webp';
import nextRepeat7Sprite from '../assets/sprites/nextRepeat7.webp';
import nextRepeatNoSprite from '../assets/sprites/nextRepeatNo.webp';
import fluencyTestStartImage from '../assets/trainers/calendar/calendar_depressed_1.webp';
import fluencyTestEndImage from '../assets/trainers/calendar/calendar_normal_1.webp';
import readingPracticeGuideImage from '../assets/players/Beep.webp';
import './PracticeSession.css';

const TRAINING_ACHIEVEMENT_DURATION_MS = 1000;
const TIME_ACHIEVEMENT_INTERVAL_SECONDS = 600;
const SESSION_TIME_LIMIT_SECONDS = 30 * 60;
const AUTO_GRADE_CUE_DURATION_MS = 2600;
const TRAINING_ACHIEVEMENT_FRAME_WIDTH = 128;
const TRAINING_ACHIEVEMENT_FRAME_HEIGHT = 128;
const MEDIA_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api\/?$/, '');
const GRADE_FOUR_MIN_REPEAT_COUNT = 5;
const FLASHCARD_REPEAT_INPUT_LOCK_MS = 560;
const AUTO_REPEAT_HOLD_MS = 1000;
const AUTO_REPEAT_GRADUATION_DELAY_MS = 850;
const QUICK_PRACTICE_REPEAT_COUNT = 4;
const QUICK_TEST_TIME_LIMITS_MS = {
  short: { 3: 6000, 4: 6000 },
  sentence: { 3: 6000, 4: 8000 },
  longSentence: { 3: 10000, 4: 12000 },
};
const LONG_QUICK_TEST_SENTENCE_MIN_WORDS = 12;
const LONG_QUICK_TEST_SENTENCE_MIN_CHARS = 80;
const NPC_ENCOUNTER_SCORE_THRESHOLD = 100;
const NPC_ENCOUNTER_MIN_ELIGIBLE_CARDS = 10;
const NPC_ENCOUNTER_CARD_COUNT = 10;
const NPC_ENCOUNTER_MIN_DAYS_SINCE_STUDIED = 15;
const FIRST_REP_BONUS_POINTS = 4;
const TEN_REP_BONUS_INTERVAL = 10;
const TEN_REP_BONUS_POINTS = 1;
const READING_MID_SENTENCE_MIN_CHARS = 50;
const READING_LONG_SENTENCE_MIN_CHARS = 100;
export const PRACTICE_MODE_COMPLETED_REVIEW = 'completedReview';

const GRADUATION_CELEBRATION_MESSAGES = {
  [PracticeSettingsService.GRADUATION_TYPES.MANUAL]: {
    title: 'practiceUi.completion.manualTitle',
    message: 'practiceUi.completion.manualMessage',
  },
  [PracticeSettingsService.GRADUATION_TYPES.SCORE]: {
    title: 'practiceUi.completion.scoreTitle',
    message: 'practiceUi.completion.scoreMessage',
  },
  [PracticeSettingsService.GRADUATION_TYPES.EARLY]: {
    title: 'practiceUi.completion.earlyTitle',
    message: 'practiceUi.completion.earlyMessage',
  },
};
const NEXT_REPEAT_SPRITES = [
  nextRepeat1Sprite,
  nextRepeat2Sprite,
  nextRepeat3Sprite,
  nextRepeat4Sprite,
  nextRepeat5Sprite,
  nextRepeat6Sprite,
  nextRepeat7Sprite,
];

function getDaysSinceLastStudied(card, now = new Date()) {
  if (!card?.lastPracticedAt) return Infinity;
  const lastPracticedAt = new Date(card.lastPracticedAt);
  if (Number.isNaN(lastPracticedAt.getTime())) return Infinity;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.floor((now.getTime() - lastPracticedAt.getTime()) / MS_PER_DAY);
}

export function isNpcEncounterEligibleCard(card, now = new Date()) {
  const cardType = PracticeSettingsService.getCardType(card);
  const isCompleted = PracticeSettingsService.isGraduated(card);

  return (
    isCompleted &&
    cardType !== PracticeSettingsService.CARD_TYPES.READING &&
    getDaysSinceLastStudied(card, now) > NPC_ENCOUNTER_MIN_DAYS_SINCE_STUDIED
  );
}

export function getNpcEncounterEligibleCards(cards = [], now = new Date()) {
  return (cards || [])
    .filter((card) => isNpcEncounterEligibleCard(card, now))
    .sort((a, b) => getDaysSinceLastStudied(b, now) - getDaysSinceLastStudied(a, now));
}

export function selectNpcEncounterCards(cards = [], now = new Date()) {
  return getNpcEncounterEligibleCards(cards, now)
    .slice(0, NPC_ENCOUNTER_CARD_COUNT);
}

export function hasEnoughNpcEncounterCards(cards = []) {
  return (cards || []).length >= NPC_ENCOUNTER_MIN_ELIGIBLE_CARDS;
}

export function getNpcEncounterResultSceneId(beforeSceneId, sceneOutcome) {
  const suffix = String(beforeSceneId || '').match(/_(\d+)$/)?.[1];
  if (!suffix || !sceneOutcome) return null;
  return `Encounter_${sceneOutcome}_${suffix}`;
}

function getAchievementSpriteConfig(spriteSrc, frameCount) {
  return {
    spriteSrc,
    frameCount,
    frameWidth: TRAINING_ACHIEVEMENT_FRAME_WIDTH,
    frameHeight: TRAINING_ACHIEVEMENT_FRAME_HEIGHT,
  };
}

export function getRandomNextRepeatSprite(random = Math.random) {
  const randomValue = Math.min(0.999999, Math.max(0, Number(random()) || 0));
  const spriteSrc = NEXT_REPEAT_SPRITES[Math.floor(randomValue * NEXT_REPEAT_SPRITES.length)];
  return getAchievementSpriteConfig(spriteSrc, 16);
}

export function shouldOfferCardMessageOnReveal({
  card,
  direction,
  from,
  to,
  reverseMode = false,
  alreadyHandled = false,
} = {}) {
  if (!card || alreadyHandled) return false;
  if (direction !== 'left') return false;

  const answerSide = reverseMode ? 'front' : 'back';
  const questionSide = reverseMode ? 'back' : 'front';
  if (from !== answerSide || to !== questionSide) return false;

  const cardType = PracticeSettingsService.getCardType(card);
  if (cardType === PracticeSettingsService.CARD_TYPES.READING) return false;

  const minimumScore = cardType === PracticeSettingsService.CARD_TYPES.SIMPLE ? 100 : 200;
  return PracticeSettingsService.getCardScore(card) >= minimumScore;
}

export function hasReachedPracticeTarget(currentRepeats, targetRepeats) {
  const target = Math.max(0, Number(targetRepeats) || 0);
  return target > 0 && (Number(currentRepeats) || 0) >= target;
}

export function getNextPracticeRepeatCount(currentRepeats, targetRepeats) {
  const current = Math.max(0, Number(currentRepeats) || 0);
  const target = Math.max(0, Number(targetRepeats) || 0);
  return target > 0 ? Math.min(current + 1, target) : current + 1;
}

export function calculatePracticeScoreBreakdown({
  practiceCount = 0,
  isFirstSessionRep = false,
  isFrontSide = false,
  level = 1,
  cardType = PracticeSettingsService.CARD_TYPES.NORMAL,
  completedReviewMode = false,
} = {}) {
  const nextPracticeCount = (Number(practiceCount) || 0) + 1;
  const isSimpleCard = cardType === PracticeSettingsService.CARD_TYPES.SIMPLE;
  let delta = 1;
  const bonuses = [];

  if (isFirstSessionRep) {
    delta += FIRST_REP_BONUS_POINTS;
    bonuses.push({ label: 'First Rep', points: FIRST_REP_BONUS_POINTS });
  }

  if (completedReviewMode) {
    return { scoreDelta: delta, bonuses };
  }

  if (isSimpleCard) {
    return { scoreDelta: delta, bonuses };
  }

  if (nextPracticeCount > 0 && nextPracticeCount % TEN_REP_BONUS_INTERVAL === 0) {
    delta += TEN_REP_BONUS_POINTS;
    bonuses.push({ label: '10 Reps', points: TEN_REP_BONUS_POINTS });
  }

  if (isFrontSide) {
    delta += 1;
    bonuses.push({ label: 'Front', points: 1 });
  }

  const levelBonus = PracticeSettingsService.getLevelBonusPoints({ level });
  if (levelBonus > 0) {
    delta += levelBonus;
    bonuses.push({ label: 'Level Bonus', points: levelBonus });
  }

  return { scoreDelta: delta, bonuses };
}

export function getReadingSentenceLengthBonus(sentenceText = '') {
  const visibleLength = String(sentenceText || '').trim().length;

  if (visibleLength >= READING_LONG_SENTENCE_MIN_CHARS) {
    return { label: 'Long Sentence', points: 2, length: visibleLength };
  }

  if (visibleLength >= READING_MID_SENTENCE_MIN_CHARS) {
    return { label: 'Mid Sentence', points: 1, length: visibleLength };
  }

  return { label: null, points: 0, length: visibleLength };
}

export function calculateReadingSentenceScoreBreakdown(sentenceText = '', repeatCount = 1) {
  const safeRepeatCount = Math.max(0, Number(repeatCount) || 0);
  const lengthBonus = getReadingSentenceLengthBonus(sentenceText);
  const perRepeatScore = 1 + lengthBonus.points;
  const scoreDelta = perRepeatScore * safeRepeatCount;
  const bonuses = [];

  if (lengthBonus.points > 0 && safeRepeatCount > 0) {
    bonuses.push({
      label: lengthBonus.label,
      points: lengthBonus.points * safeRepeatCount,
    });
  }

  return {
    scoreDelta,
    perRepeatScore,
    bonuses,
    length: lengthBonus.length,
  };
}

export function allocateReadingPracticeScore(earnedScore = 0, cardScoreDelta = 0) {
  return {
    sessionScoreDelta: Math.max(0, Number(earnedScore) || 0),
    cardScoreDelta: Math.max(0, Number(cardScoreDelta) || 0),
  };
}

export function calculateReadingPracticeScoreBreakdown({
  sentenceText = '',
  repeatCount = 1,
  itemType = 'story',
  speechSide = null,
  completedWithoutFlip = false,
} = {}) {
  const safeRepeatCount = Math.max(0, Number(repeatCount) || 0);

  if (itemType === 'speech') {
    const perRepeatScore = speechSide === 'question' ? 2 : 1;
    const noFlipBonus = completedWithoutFlip ? 4 : 0;
    return {
      scoreDelta: (perRepeatScore * safeRepeatCount) + noFlipBonus,
      perRepeatScore,
      bonuses: noFlipBonus > 0 ? [{ label: 'No-Flip Bonus', points: noFlipBonus }] : [],
      length: String(sentenceText || '').trim().length,
    };
  }

  return calculateReadingSentenceScoreBreakdown(sentenceText, safeRepeatCount);
}

export function calculateAutoRepeatScoreBreakdown({
  practiceCount = 0,
  repeatCount = 0,
  isFirstSessionRep = false,
  isFrontSide = false,
  level = 1,
  cardType = PracticeSettingsService.CARD_TYPES.NORMAL,
  completedReviewMode = false,
} = {}) {
  const safeRepeatCount = Math.max(0, Number(repeatCount) || 0);
  const bonusTotals = new Map();
  let scoreDelta = 0;

  for (let index = 0; index < safeRepeatCount; index += 1) {
    const breakdown = calculatePracticeScoreBreakdown({
      practiceCount: (Number(practiceCount) || 0) + index,
      isFirstSessionRep: isFirstSessionRep && index === 0,
      isFrontSide,
      level,
      cardType,
      completedReviewMode,
    });

    scoreDelta += breakdown.scoreDelta;
    breakdown.bonuses.forEach((bonus) => {
      bonusTotals.set(bonus.label, (bonusTotals.get(bonus.label) || 0) + bonus.points);
    });
  }

  const bonuses = safeRepeatCount > 1
    ? [{ label: 'Auto Repeat', points: scoreDelta }]
    : [];

  bonusTotals.forEach((points, label) => {
    bonuses.push({ label, points });
  });

  return { scoreDelta, bonuses };
}

export function calculateAutoRepeatProgress({
  practiceCount = 0,
  repeatCount = 0,
  maxScoreGain = Infinity,
  isFirstSessionRep = false,
  isFrontSide = false,
  level = 1,
  cardType = PracticeSettingsService.CARD_TYPES.NORMAL,
  completedReviewMode = false,
} = {}) {
  const safeRepeatCount = Math.max(0, Number(repeatCount) || 0);
  const safeMaxScoreGain = Number.isFinite(Number(maxScoreGain))
    ? Math.max(0, Number(maxScoreGain))
    : Infinity;
  const bonusTotals = new Map();
  let scoreDelta = 0;
  let actualRepeatCount = 0;
  let cappedByScore = false;

  for (let index = 0; index < safeRepeatCount; index += 1) {
    if (scoreDelta >= safeMaxScoreGain) {
      cappedByScore = true;
      break;
    }

    const breakdown = calculatePracticeScoreBreakdown({
      practiceCount: (Number(practiceCount) || 0) + index,
      isFirstSessionRep: isFirstSessionRep && index === 0,
      isFrontSide,
      level,
      cardType,
      completedReviewMode,
    });
    const limitedDelta = Math.min(breakdown.scoreDelta, safeMaxScoreGain - scoreDelta);

    if (limitedDelta <= 0) {
      cappedByScore = true;
      break;
    }

    actualRepeatCount += 1;
    scoreDelta += limitedDelta;

    if (limitedDelta < breakdown.scoreDelta) {
      cappedByScore = true;
      break;
    }

    breakdown.bonuses.forEach((bonus) => {
      bonusTotals.set(bonus.label, (bonusTotals.get(bonus.label) || 0) + bonus.points);
    });
  }

  const bonuses = scoreDelta > 0
    ? [{ label: 'Auto Repeat', points: scoreDelta }]
    : [];

  if (!cappedByScore) {
    bonusTotals.forEach((points, label) => {
      bonuses.push({ label, points });
    });
  }

  return { repeatCount: actualRepeatCount, scoreDelta, bonuses };
}

export function calculateAnswerRevealAutoGrade(currentGrade) {
  return 1;
}

export function isQuickTestGradeEligible(currentGrade) {
  const grade = Number(currentGrade) || 0;
  return grade === 3 || grade === 4;
}

export function isLongQuickTestSentence(card) {
  if (PracticeSettingsService.getCardType(card) !== PracticeSettingsService.CARD_TYPES.NORMAL) {
    return false;
  }

  const answer = PracticeSettingsService.getPracticeCardContent(card).answer;
  const visibleText = stripSimpleMarkdown(answer).replace(/\s+/g, ' ').trim();
  const wordCount = visibleText.match(/[\p{L}\p{N}]+(?:['\u2019-][\p{L}\p{N}]+)*/gu)?.length || 0;

  return (
    wordCount >= LONG_QUICK_TEST_SENTENCE_MIN_WORDS ||
    visibleText.length >= LONG_QUICK_TEST_SENTENCE_MIN_CHARS
  );
}

export function getQuickTestTimeLimitMs(cardOrGrade) {
  const isCard = Boolean(cardOrGrade && typeof cardOrGrade === 'object');
  const grade = isCard
    ? PracticeSettingsService.getAssessmentGrade(cardOrGrade)
    : Number(cardOrGrade) || 0;

  if (!isQuickTestGradeEligible(grade)) return 0;

  // Numeric calls retain the regular Sentence defaults for backwards compatibility.
  if (!isCard) return QUICK_TEST_TIME_LIMITS_MS.sentence[grade];

  const cardType = PracticeSettingsService.getCardType(cardOrGrade);
  if (cardType === PracticeSettingsService.CARD_TYPES.READING) return 0;
  if (cardType === PracticeSettingsService.CARD_TYPES.SIMPLE) {
    return QUICK_TEST_TIME_LIMITS_MS.short[grade];
  }
  if (isLongQuickTestSentence(cardOrGrade)) {
    return QUICK_TEST_TIME_LIMITS_MS.longSentence[grade];
  }

  return QUICK_TEST_TIME_LIMITS_MS.sentence[grade];
}

export function calculateNoFlipCompletionAutoGrade(
  currentGrade,
  quickTestPassed = false
) {
  return isQuickTestGradeEligible(currentGrade) && quickTestPassed ? 4 : 3;
}

export function getEarlyGradeThreeScoreThreshold(card) {
  const cardType = PracticeSettingsService.getCardType(card);
  if (cardType === PracticeSettingsService.CARD_TYPES.READING) return null;
  return cardType === PracticeSettingsService.CARD_TYPES.SIMPLE ? 50 : 100;
}

export function shouldShowEarlyGradeThreePopup(card, previousGrade, nextGrade) {
  const threshold = getEarlyGradeThreeScoreThreshold(card);
  if (threshold === null) return false;

  const safePreviousGrade = Number.isFinite(Number(previousGrade)) ? Number(previousGrade) : 0;
  const safeNextGrade = Number.isFinite(Number(nextGrade)) ? Number(nextGrade) : 0;
  return (
    safePreviousGrade < 3 &&
    safeNextGrade === 3 &&
    PracticeSettingsService.getCardScore(card) < threshold
  );
}

export function getReadingAutoGrade(projectedPracticeCount) {
  return PracticeSettingsService.getReadingAutoGrade(projectedPracticeCount);
}

function getRepeatGradeForCard(card) {
  return PracticeSettingsService.getPracticeIntensityLevel(card);
}

export function getPracticeTargetRepeats(card, isQuickMode = false) {
  if (PracticeSettingsService.getCardType(card) === PracticeSettingsService.CARD_TYPES.READING && card?.repeatsPerSession === 4) return 4;
  return isQuickMode
    ? QUICK_PRACTICE_REPEAT_COUNT
    : AutonomousSchedulerService.getRepeatCountForGrade(getRepeatGradeForCard(card), card);
}

function isReadableNoteName(value, noteId) {
  if (!value || typeof value !== 'string') return false;

  const trimmed = value.trim();
  if (!trimmed) return false;
  if (noteId && trimmed === noteId) return false;
  if (noteId && trimmed === `Note ${noteId}`) return false;

  return true;
}

function collectNoteNamesFromCards(cards = []) {
  return cards.reduce((names, card) => {
    const noteId = card?.noteId;
    const candidate =
      card?.noteTitle ||
      card?.noteName ||
      card?.note?.title ||
      card?.note?.name;

    if (noteId && isReadableNoteName(candidate, noteId)) {
      names[noteId] = candidate.trim();
    }

    return names;
  }, {});
}

function collectNoteNamesFromMetadata(notes = []) {
  return notes.reduce((names, note) => {
    const noteId = note?.noteId || note?.id;
    const candidate = note?.noteTitle || note?.title || note?.name;

    if (noteId && isReadableNoteName(candidate, noteId)) {
      names[noteId] = candidate.trim();
    }

    return names;
  }, {});
}

function getCurrentNoteName(card, noteNameById) {
  if (!card) return '';

  const directNames = collectNoteNamesFromCards([card]);
  if (card.noteId && directNames[card.noteId]) {
    return directNames[card.noteId];
  }

  return card.noteId ? (noteNameById[card.noteId] || '') : '';
}

function resolveNoteCoverUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('http')) return url;
  return `${MEDIA_ORIGIN}${url}`;
}

function collectNoteCoversFromMetadata(notes = []) {
  return notes.reduce((covers, note) => {
    const noteId = note?.noteId || note?.id;
    const coverUrl = resolveNoteCoverUrl(note?.coverUrl);

    if (noteId && coverUrl) {
      covers[noteId] = coverUrl;
    }

    return covers;
  }, {});
}

function getCurrentNoteCover(card, noteCoverById) {
  if (!card?.noteId) return '';
  return noteCoverById[card.noteId] || '';
}

function createReadingIntroductionPopup() {
  return {
    phaseTransition: 'readingStart',
    title: t('practicePhases.reading.title'),
    message: t('practicePhases.reading.start'),
    iconSrc: readingPracticeGuideImage,
    iconAlt: t('practicePhases.reading.startImageAlt'),
    iconLayout: 'speech',
  };
}

function PracticeSession({ flashCards, onSessionComplete, onFlashCardsUpdate, settings = {}, practiceScope = 'all', practiceStartId = null, canUseTts = true, onImportCard, isCardImported, externalPause = false }) {
  const isQuickMode = settings.practiceMode === 'quick';
  const isWritingReview = isQuickMode && settings.writingReview === true;
  const getTargetRepeatsForCard = (card) => getPracticeTargetRepeats(card, isQuickMode);
  const { confirm, alert: showAlert, confirmDialog, isOpen: isConfirmDialogOpen } = useConfirmDialog();
  const reverseMode = false;
  const isCompletedReviewMode = settings.practiceMode === PRACTICE_MODE_COMPLETED_REVIEW;
  const { scenes: coachingScenes } = useLearningCoaching('practice', { enabled: !isQuickMode && !isCompletedReviewMode });
  const coachingSelector = useRef(null);
  if (!coachingSelector.current) coachingSelector.current = createCoachingSelector();
  const isFullPractice = practiceScope === 'all' && !isQuickMode && !isCompletedReviewMode;
  const practiceCardTypeFilter = PracticeSettingsService.normalizePracticeCardTypeFilter(settings.practiceCardTypeFilter);
  const maxScore = PracticeSettingsService.normalizeMaxScore(settings.maxScore ?? settings.maxRepeatCount);
  const maxExcludeGrade = PracticeSettingsService.normalizeMaxExcludeGrade(
    settings.maxExcludeGrade,
    PracticeSettingsService.getMaxExcludeGradeFromExcludedGrades(settings.excludedGrades)
  );
  const excludedGradesForFinish = PracticeSettingsService.getExcludedGradesFromMax(maxExcludeGrade);
  const isGradeFinished = (grade) => excludedGradesForFinish.includes(Number(grade));
  const isEligibleChild = (card) => (
    card?.practiceAvailable !== false &&
    PracticeSettingsService.matchesPracticeCardTypeFilter(card, practiceCardTypeFilter) &&
    PracticeSettingsService.isUnderMaxScore(card, maxScore) &&
    !PracticeSettingsService.isLearningCurveHidden(card) &&
    !PracticeSettingsService.isPracticedToday(card) &&
    !isGradeFinished(PracticeSettingsService.getAssessmentGrade(card))
  );
  const isEligibleForPractice = (card) => {
    if (card?.isReadingGroup && Array.isArray(card.readingChildren)) {
      return isCompletedReviewMode
        ? card.readingChildren.some((child) => (
          child.practiceAvailable !== false && PracticeSettingsService.isGraduated(child)
        ))
        : card.readingChildren.some(isEligibleChild);
    }
    return isCompletedReviewMode
      ? PracticeSettingsService.isGraduated(card)
      : isEligibleChild(card);
  };
  const repeatEligibleFlashCards = (flashCards || []).filter((card) =>
    isEligibleForPractice(card)
  );
  const initialQueueRef = useRef({ resolved: false, cards: [] });

  if (!initialQueueRef.current.resolved) {
    initialQueueRef.current = {
      resolved: true,
      cards: PracticeSettingsService.createPracticePhaseQueue(isFullPractice ? repeatEligibleFlashCards : repeatEligibleFlashCards.map(card => ({ ...card, practicePhase: PracticeSettingsService.PRACTICE_PHASES.CORE_TRAINING }))),
    };
  }

  const memoryTrialTargetIdsRef = useRef(null);
  if (!memoryTrialTargetIdsRef.current) {
    memoryTrialTargetIdsRef.current = new Set(initialQueueRef.current.cards
      .filter(card => PracticeSettingsService.getPracticePhase(card) === PracticeSettingsService.PRACTICE_PHASES.MEMORY_TRIAL &&
        getQuickTestTimeLimitMs(card) > 0)
      .map(card => String(card.id ?? card._id)));
  }

  const initialCardRef = useRef({ resolved: false, card: null });

  if (!initialCardRef.current.resolved) {
    initialCardRef.current = {
      resolved: true,
      card: initialQueueRef.current.cards[0] || null,
    };
  }

  const initialPhasePopupRef = useRef({ resolved: false, popup: null });
  if (!initialPhasePopupRef.current.resolved) {
    const memoryTrialCount = memoryTrialTargetIdsRef.current.size;
    initialPhasePopupRef.current = {
      resolved: true,
      popup: isFullPractice && memoryTrialCount > 0
        ? {
          id: 0,
          phaseTransition: 'memoryTrialStart',
          title: t('practicePhases.memoryTrial.title'),
          message: t('practicePhases.memoryTrial.start', undefined, { count: memoryTrialCount }),
          iconSrc: fluencyTestStartImage,
          iconAlt: t('practicePhases.memoryTrial.startImageAlt'),
          iconLayout: 'speech',
        }
        : isFullPractice && PracticeSettingsService.getCardType(initialCardRef.current.card) === PracticeSettingsService.CARD_TYPES.READING
          ? { ...createReadingIntroductionPopup(), id: 0 }
          : null,
    };
  }

  const [cardPool, setCardPool] = useState(flashCards);
  const [sessionQueue, setSessionQueue] = useState(() => initialQueueRef.current.cards);
  const [sessionQueueIndex, setSessionQueueIndex] = useState(0);
  const [currentCard, setCurrentCard] = useState(() => initialCardRef.current.card);
  const readingAudio = useReadingAudio(currentCard?.cardType === 'reading' ? (currentCard?.id || currentCard?._id) : null,
    JSON.stringify([currentCard?.readingSegments, currentCard?.readingContent, currentCard?.back]));
  const [recentCardIds, setRecentCardIds] = useState([]);
  const [currentCardSessionRepeats, setCurrentCardSessionRepeats] = useState(0);
  const [readingProgressPercent, setReadingProgressPercent] = useState(0);
  const [activeReadingSentence, setActiveReadingSentence] = useState(null);
  const [currentCardSessionScoreGained, setCurrentCardSessionScoreGained] = useState(0);
  const [currentScoreBonusEvent, setCurrentScoreBonusEvent] = useState({ id: 0, bonuses: [] });
  const [currentCardTargetRepeats, setCurrentCardTargetRepeats] = useState(() => {
    return getTargetRepeatsForCard(initialCardRef.current.card);
  });

  const [isRevealed, setIsRevealed] = useState(false);
  const [hasAnswerDecision, setHasAnswerDecision] = useState(false);
  const [isTtsAnswerVisible, setIsTtsAnswerVisible] = useState(reverseMode);
  const [sessionResults, setSessionResults] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRating, setSelectedRating] = useState(1);
  const [isGradeUpdated, setIsGradeUpdated] = useState(false);
  const [slideDirection, setSlideDirection] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [totalRepeatCount, setTotalRepeatCount] = useState(0);
  const [totalScoreGained, setTotalScoreGained] = useState(0);
  const [uniqueCardsStudied, setUniqueCardsStudied] = useState(() => new Set());
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [reviewCard, setReviewCard] = useState(null);
  const [importCard, setImportCard] = useState(null);
  const [importedCardIds, setImportedCardIds] = useState(() => new Set());
  const importPaused = externalPause || Boolean(importCard);
  const [cardMenuOpen, setCardMenuOpen] = useState(false);
  const [isLevelUpModalOpen, setIsLevelUpModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [activeCelebrationPopup, setActiveCelebrationPopup] = useState(
    () => initialPhasePopupRef.current.popup
  );
  const achievementOverlay = activeCelebrationPopup?.kind === 'advanceAchievement'
    ? activeCelebrationPopup
    : null;
  const [cardHistory, setCardHistory] = useState([]);
  const [navigationStack, setNavigationStack] = useState(() => initialCardRef.current.card ? [initialCardRef.current.card] : []);
  const [navigationIndex, setNavigationIndex] = useState(0);
  const [displayedNavigationIndex, setDisplayedNavigationIndex] = useState(0);
  const [noteNameById, setNoteNameById] = useState(() => collectNoteNamesFromCards(flashCards));
  const [noteCoverById, setNoteCoverById] = useState({});
  const [pendingPracticeSaves, setPendingPracticeSaves] = useState(0);
  const [boardBreakPopup, setBoardBreakPopup] = useState(null);
  const [autoGradeCue, setAutoGradeCue] = useState(null);
  const [readingResetKey, setReadingResetKey] = useState(0);
  const completedReadingVisitRef = useRef(null);
  const [npcEncounter, setNpcEncounter] = useState(null);
  const [quickTestTimeLimitExpired, setQuickTestTimeLimitExpired] = useState(false);
  const [quickTestEligible, setQuickTestEligible] = useState(false);
  const [quickTestPassed, setQuickTestPassed] = useState(false);
  const [quickTestRemainingMs, setQuickTestRemainingMs] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(() => isPracticeSoundEnabled() && isTtsAutoplayEnabled());
  const playAnswerFeedback = useAnswerFeedbackAudio(soundEnabled);
  const playCardFlip = useCardFlipAudio(soundEnabled);
  const answerFeedbackFlipRef = useRef(false);
  const ttsAutoplayEnabled = soundEnabled;
  useEffect(() => {
    setPracticeSoundEnabled(soundEnabled);
    setTtsAutoplayEnabled(soundEnabled);
  }, [soundEnabled]);
  const [isTtsInteractionLocked, setIsTtsInteractionLocked] = useState(false);
  const [playerLevel, setPlayerLevel] = useState(null);
  const [messageButtonCardId, setMessageButtonCardId] = useState(null);
  const [currentCardHintMessages, setCurrentCardHintMessages] = useState([]);
  const [displayedCardStats, setDisplayedCardStats] = useState({
    practiceCount: initialCardRef.current.card?.practiceCount || 0,
    score: initialCardRef.current.card?.score || 0,
    daysSince: null,
  });
  const arePracticeTimersPaused = Boolean(
    importPaused || activeCelebrationPopup || isConfirmDialogOpen || isEditModalOpen || reviewCard || cardMenuOpen || isLevelUpModalOpen ||
    isProcessing || Boolean(slideDirection) || error?.sessionEndFailed || (npcEncounter && npcEncounter.phase !== 'battle')
  );

  const handleReadingProgressChange = useCallback(({ percent }) => {
    const normalizedPercent = Math.min(100, Math.max(0, Number(percent) || 0));
    setReadingProgressPercent((previous) => (
      previous === normalizedPercent ? previous : normalizedPercent
    ));
  }, []);

  // Refs for use inside async callbacks and timeouts
  const cardPoolRef = useRef(cardPool);
  const sessionQueueRef = useRef(sessionQueue);
  const sessionQueueIndexRef = useRef(sessionQueueIndex);
  const currentCardRef = useRef(currentCard);
  const recentCardIdsRef = useRef(recentCardIds);
  const sessionResultsRef = useRef(sessionResults);
  const elapsedTimeRef = useRef(elapsedTime);
  const sessionElapsedMsRef = useRef(0);
  const practiceTimersPausedRef = useRef(arePracticeTimersPaused);
  practiceTimersPausedRef.current = arePracticeTimersPaused;
  const totalRepeatCountRef = useRef(totalRepeatCount);
  const totalScoreGainedRef = useRef(totalScoreGained);
  const currentCardSessionRepeatsRef = useRef(currentCardSessionRepeats);
  const flashcardRepeatInputLockedRef = useRef(false);
  const isTtsInteractionLockedRef = useRef(false);
  const flashcardRepeatInputLockTimerRef = useRef(null);
  const currentCardSessionScoreGainedRef = useRef(currentCardSessionScoreGained);
  const scoreBonusEventIdRef = useRef(0);
  const currentCardTargetRepeatsRef = useRef(currentCardTargetRepeats);
  const currentCardDisplayDaysSinceRef = useRef(null);
  const cardHistoryRef = useRef([]);
  const navigationStackRef = useRef(initialCardRef.current.card ? [initialCardRef.current.card] : []);
  const navigationIndexRef = useRef(0);
  const pendingCardProgressRef = useRef(new Map());
  const progressFlushPromiseRef = useRef(null);
  const sessionStartedAtRef = useRef(new Date().toISOString());
  const practiceSessionRecordIdRef = useRef(createPracticeSessionRecordId(practiceStartId, 'practice'));
  const lastSavedCheckpointRef = useRef(0);
  const checkpointSavePromiseRef = useRef(null);
  const persistCheckpointRef = useRef(null);
  const targetAdvanceTimerRef = useRef(null);
  const cardDepartureWaitRef = useRef(null);
  const departedDisplayTokenRef = useRef(null);
  const cardAdvancePendingRef = useRef(false);
  const pendingAdvanceSuppressNotificationRef = useRef(false);
  const sessionEndTimerRef = useRef(null);
  const performEndSessionRef = useRef(null);
  const activeCelebrationPopupRef = useRef(initialPhasePopupRef.current.popup);
  const readingIntroductionShownRef = useRef(initialPhasePopupRef.current.popup?.phaseTransition === 'readingStart');
  const queuedCelebrationPopupsRef = useRef([]);
  const pendingCardAdvanceContinuationRef = useRef(null);
  const cardAdvanceRequestIdRef = useRef(0);
  const pendingSessionCompletionRef = useRef(null);
  const celebrationPopupIdRef = useRef(0);
  const celebrationTransitionRef = useRef(false);
  const nextCelebrationTimerRef = useRef(null);
  const showNextCelebrationPopupRef = useRef(null);
  const isMountedRef = useRef(true);
  const finishedCardIdsRef = useRef(new Set());
  const boardBreakPopupOpenRef = useRef(false);
  const boardBreakShownIdsRef = useRef(new Set());
  const practicedCardCountRef = useRef(0);
  const currentCardDisplayTokenRef = useRef(0);
  const editingCardRef = useRef(null);
  const currentCardDisplayIdRef = useRef(null);
  const practicedDisplayTokensRef = useRef(new Set());
  const lastCountedPracticeDisplayTokenRef = useRef(null);
  const currentCardDisplayPracticeIncrementsRef = useRef(0);
  const sessionScoredCardIdsRef = useRef(new Set());
  const achievementActiveRef = useRef(false);
  const lastTimeAchievementMinutesRef = useRef(0);
  const seenCycleIdsRef = useRef(new Set());
  const selectedRatingRef = useRef(selectedRating);
  const answerViewedRef = useRef(false);
  const answerOkRef = useRef(false);
  const manualGradeOverrideRef = useRef(false);
  const answerDecisionMadeRef = useRef(false);
  const pendingCardEndGradeRef = useRef(null);
  const answerRevealGradeAppliedRef = useRef(false);
  const messageButtonRevealHandledRef = useRef(false);
  const cueTimerRef = useRef(null);
  const gradeCueSequenceRef = useRef(0);
  const quickTestTimerRef = useRef(null);
  const quickTestIntervalRef = useRef(null);
  const quickTestEligibleRef = useRef(false);
  const quickTestPassedRef = useRef(false);
  const quickTestFirstRepeatObservedRef = useRef(false);
  const quickTestTimeLimitExpiredRef = useRef(false);
  const quickTestRemainingMsRef = useRef(0);
  const quickTestDeadlineRef = useRef(null);
  const memoryTrialResultsRef = useRef(new Map());
  const advanceInFlightRef = useRef(false);
  const sessionLimitReachedRef = useRef(false);
  const sessionLimitEndingRef = useRef(false);
  const pendingSessionEndReasonRef = useRef(null);
  const timeCompletionCelebrationShownRef = useRef(false);
  const queueExhaustedCelebrationShownRef = useRef(false);
  const npcEncounterCheckedRef = useRef(false);
  const npcEncounterRef = useRef(null);
  const npcJudgedCardIdsRef = useRef(new Set());
  const pendingAdvanceAfterEncounterRef = useRef(null);
  const currentCardStartedUngradedRef = useRef(
    PracticeSettingsService.getAssessmentGrade(initialCardRef.current.card) === 0
  );
  const uniqueCardsStudiedRef = useRef(uniqueCardsStudied);

  const getCardId = (card) => card?.id ?? card?._id;
  const getCurrentUserId = () => authService.getCurrentUserId?.() || 'anonymous';

  const handleTtsInteractionLockChange = useCallback((locked) => {
    const normalized = Boolean(locked);
    isTtsInteractionLockedRef.current = normalized;
    setIsTtsInteractionLocked(normalized);
  }, []);

  const clearMessageButton = useCallback(() => {
    setMessageButtonCardId(null);
  }, []);

  const clearFlashcardRepeatInputLock = useCallback(() => {
    flashcardRepeatInputLockedRef.current = false;
    if (flashcardRepeatInputLockTimerRef.current) {
      clearTimeout(flashcardRepeatInputLockTimerRef.current);
      flashcardRepeatInputLockTimerRef.current = null;
    }
  }, []);

  const startFlashcardRepeatInputLock = useCallback(() => {
    clearFlashcardRepeatInputLock();
    flashcardRepeatInputLockedRef.current = true;
    flashcardRepeatInputLockTimerRef.current = setTimeout(() => {
      flashcardRepeatInputLockedRef.current = false;
      flashcardRepeatInputLockTimerRef.current = null;
    }, FLASHCARD_REPEAT_INPUT_LOCK_MS);
  }, [clearFlashcardRepeatInputLock]);

  const loadHintMessagesForCard = useCallback((card) => {
    const cardId = getCardId(card);
    if (!cardId) {
      setCurrentCardHintMessages([]);
      return;
    }
    setCurrentCardHintMessages(CardMessageService.getMessages({
      userId: getCurrentUserId(),
      cardId,
    }));
  }, []);

  const saveHintMessagesForCurrentCard = useCallback((messages) => {
    if (isQuickMode) { setCurrentCardHintMessages(messages); setMessageButtonCardId(null); return; }
    const cardId = getCardId(currentCardRef.current);
    if (!cardId) return;
    CardMessageService.saveMessages({
      userId: getCurrentUserId(),
      cardId,
      messages,
    });
    setCurrentCardHintMessages(CardMessageService.getMessages({
      userId: getCurrentUserId(),
      cardId,
    }));
    setMessageButtonCardId(null);
  }, [isQuickMode]);

  const markCardStudied = useCallback((cardId) => {
    if (!cardId) return;

    const next = new Set(uniqueCardsStudiedRef.current);
    next.add(cardId);
    uniqueCardsStudiedRef.current = next;
    setUniqueCardsStudied(next);
  }, []);

  const countPendingBufferedOps = useCallback(() => (
    Array.from(pendingCardProgressRef.current.values()).reduce((total, entry) => (
      total + (entry.practiceCountDelta || 0) + (entry.assessments?.length || 0)
      + (entry.scoreDelta || 0)
      + ((entry.testIndex !== null && entry.testIndex !== undefined) ? 1 : 0)
      + (PracticeSettingsService.normalizeGraduationType(entry.graduationType) ? 1 : 0)
      + (entry.completedAt ? 1 : 0)
      + (entry.lastPracticedAt ? 1 : 0)
    ), 0)
  ), []);

  const syncPendingProgressCount = useCallback(() => {
    if (isMountedRef.current) {
      setPendingPracticeSaves(countPendingBufferedOps());
    }
  }, [countPendingBufferedOps]);

  const updatePendingCardProgress = useCallback((cardId, updater) => {
    if (isQuickMode) return;
    const current = pendingCardProgressRef.current.get(cardId) || {
      id: cardId,
      practiceCountDelta: 0,
      scoreDelta: 0,
      practiceCountFloor: null,
      testIndex: null,
      graduationType: null,
      completedAt: null,
      lastPracticedAt: null,
      progressedAt: null,
      assessments: [],
    };
    const pendingNext = updater(current);
    const hasLearningProgress = Boolean(
      (pendingNext?.practiceCountDelta || 0) > 0 ||
      (pendingNext?.scoreDelta || 0) > 0 ||
      (pendingNext?.assessments?.length || 0) > 0
    );
    const next = hasLearningProgress && !pendingNext?.progressedAt
      ? {
          ...pendingNext,
          progressedAt: pendingNext?.assessments?.[0]?.assessedAt || new Date().toISOString(),
        }
      : pendingNext;

    if (
      !next ||
      ((!next.practiceCountDelta || next.practiceCountDelta <= 0) &&
        (!next.scoreDelta || next.scoreDelta <= 0) &&
        (next.practiceCountFloor === null || next.practiceCountFloor === undefined) &&
        (next.testIndex === null || next.testIndex === undefined) &&
        !PracticeSettingsService.normalizeGraduationType(next.graduationType) &&
        !next.completedAt &&
        (!next.assessments || next.assessments.length === 0) &&
        !next.lastPracticedAt)
    ) {
      pendingCardProgressRef.current.delete(cardId);
    } else {
      pendingCardProgressRef.current.set(cardId, next);
    }

    syncPendingProgressCount();
  }, [syncPendingProgressCount, isQuickMode]);

  const markCurrentCardPracticed = () => {
    markCardStudied(getCardId(currentCardRef.current));
    practicedDisplayTokensRef.current.add(currentCardDisplayTokenRef.current);
  };

  const showNextCelebrationPopup = () => {
    if (
      !isMountedRef.current ||
      activeCelebrationPopupRef.current ||
      celebrationTransitionRef.current ||
      importPaused || isConfirmDialogOpen || isEditModalOpen || reviewCard || cardMenuOpen || isLevelUpModalOpen || npcEncounterRef.current
    ) return;

    const nextPopup = queuedCelebrationPopupsRef.current.shift() || null;
    activeCelebrationPopupRef.current = nextPopup;
    achievementActiveRef.current = nextPopup?.kind === 'advanceAchievement';
    setActiveCelebrationPopup(nextPopup);

    if (!nextPopup && pendingSessionCompletionRef.current) {
      // Final grading can enqueue a milestone even when no end-of-session popup is needed.
      const complete = pendingSessionCompletionRef.current;
      pendingSessionCompletionRef.current = null;
      cardAdvancePendingRef.current = false;
      complete();
      return;
    }

    if (!nextPopup && pendingCardAdvanceContinuationRef.current) {
      const continueAdvance = pendingCardAdvanceContinuationRef.current;
      pendingCardAdvanceContinuationRef.current = null;
      continueAdvance();
      return;
    }

    if (!nextPopup && cardAdvancePendingRef.current) {
      scheduleAdvanceAfterTarget();
    }
  };
  showNextCelebrationPopupRef.current = showNextCelebrationPopup;

  useEffect(() => {
    showNextCelebrationPopupRef.current?.();
  }, [isConfirmDialogOpen, isEditModalOpen, reviewCard, cardMenuOpen, isLevelUpModalOpen, npcEncounter, importPaused]);

  const enqueueCelebrationPopup = useCallback((popup) => {
    if (!popup || !isMountedRef.current) return;
    queuedCelebrationPopupsRef.current.push({ ...popup, id: ++celebrationPopupIdRef.current });
    showNextCelebrationPopupRef.current?.();
  }, []);

  useEffect(() => {
    if (
      !isFullPractice ||
      readingIntroductionShownRef.current ||
      PracticeSettingsService.getCardType(currentCard) !== PracticeSettingsService.CARD_TYPES.READING ||
      activeCelebrationPopup || importPaused || isConfirmDialogOpen || isEditModalOpen ||
      reviewCard || cardMenuOpen || isLevelUpModalOpen || npcEncounter
    ) return;

    // Session-local: later reading cards and history navigation must not repeat the introduction.
    readingIntroductionShownRef.current = true;
    enqueueCelebrationPopup(createReadingIntroductionPopup());
  }, [isFullPractice, currentCard, activeCelebrationPopup, importPaused, isConfirmDialogOpen, isEditModalOpen,
    reviewCard, cardMenuOpen, isLevelUpModalOpen, npcEncounter, enqueueCelebrationPopup]);

  const handleActiveCelebrationPopupClose = useCallback((popupId) => {
    const closingPopup = activeCelebrationPopupRef.current;
    if (!isMountedRef.current || !closingPopup || closingPopup.id !== popupId) return;

    // Hold the queue while the callback runs: it may enqueue another popup.
    celebrationTransitionRef.current = true;
    activeCelebrationPopupRef.current = null;
    achievementActiveRef.current = false;
    setActiveCelebrationPopup(null);

    try {
      closingPopup.onClose?.();
    } finally {
      if (isMountedRef.current) {
        nextCelebrationTimerRef.current = setTimeout(() => {
          nextCelebrationTimerRef.current = null;
          celebrationTransitionRef.current = false;
          showNextCelebrationPopupRef.current?.();
        }, 0);
      }
    }
  }, []);

  const closeCurrentCelebrationPopup = useCallback(() => {
    handleActiveCelebrationPopupClose(activeCelebrationPopup?.id);
  }, [activeCelebrationPopup?.id, handleActiveCelebrationPopupClose]);

  const releaseAnswerChoiceModeAfterPracticeCount = () => {
    if (isCompletedReviewMode || answerDecisionMadeRef.current || (!answerViewedRef.current && !isRevealed)) {
      return;
    }

    answerDecisionMadeRef.current = true;
    setHasAnswerDecision(true);
  };

  const clearAutoGradeCue = useCallback(() => {
    if (cueTimerRef.current) {
      clearTimeout(cueTimerRef.current);
      cueTimerRef.current = null;
    }
    setAutoGradeCue(null);
  }, []);

  const showAutoGradeCue = useCallback((message, grade = null) => {
    clearAutoGradeCue();
    setAutoGradeCue({
      id: ++gradeCueSequenceRef.current,
      message,
      grade,
      cardId: String(currentCardRef.current?.id ?? currentCardRef.current?._id ?? ''),
      visitToken: currentCardDisplayTokenRef.current,
    });
    cueTimerRef.current = setTimeout(() => {
      cueTimerRef.current = null;
      setAutoGradeCue(null);
    }, AUTO_GRADE_CUE_DURATION_MS);
  }, [clearAutoGradeCue]);

  const clearQuickTestTimer = useCallback(() => {
    if (quickTestDeadlineRef.current !== null) {
      quickTestRemainingMsRef.current = Math.max(0, quickTestDeadlineRef.current - Date.now());
      quickTestDeadlineRef.current = null;
    }
    if (quickTestTimerRef.current) {
      clearTimeout(quickTestTimerRef.current);
      quickTestTimerRef.current = null;
    }
    if (quickTestIntervalRef.current) {
      clearInterval(quickTestIntervalRef.current);
      quickTestIntervalRef.current = null;
    }
  }, []);

  const recordMemoryTrialResult = useCallback((success) => {
    const card = currentCardRef.current;
    const id = String(card?.id ?? card?._id);
    if (isCompletedReviewMode || isQuickMode ||
      navigationIndexRef.current < navigationStackRef.current.length - 1 ||
      !memoryTrialTargetIdsRef.current.has(id) || memoryTrialResultsRef.current.has(id)) return;
    memoryTrialResultsRef.current.set(id, success);
  }, [isCompletedReviewMode, isQuickMode]);

  const getMemoryTrialCompletionMessage = (key) => {
    const results = [...memoryTrialResultsRef.current.values()];
    const success = results.filter(Boolean).length;
    return t(key, undefined, { success, fail: results.length - success });
  };

  const disableQuickTestTimerForCurrentCard = useCallback(() => {
    recordMemoryTrialResult(false);
    clearQuickTestTimer();
    quickTestEligibleRef.current = false;
    quickTestPassedRef.current = false;
    quickTestFirstRepeatObservedRef.current = false;
    quickTestTimeLimitExpiredRef.current = false;
    quickTestRemainingMsRef.current = 0;
    setQuickTestEligible(false);
    setQuickTestPassed(false);
    setQuickTestRemainingMs(0);
    setQuickTestTimeLimitExpired(false);
  }, [clearQuickTestTimer, recordMemoryTrialResult]);

  const resumeQuickTestTimer = useCallback(() => {
    if (
      practiceTimersPausedRef.current || !quickTestEligibleRef.current ||
      quickTestPassedRef.current || quickTestTimeLimitExpiredRef.current ||
      quickTestFirstRepeatObservedRef.current || answerViewedRef.current ||
      quickTestTimerRef.current || quickTestIntervalRef.current
    ) return;

    const remainingMs = quickTestRemainingMsRef.current;
    const expire = () => {
      recordMemoryTrialResult(false);
      clearQuickTestTimer();
      quickTestRemainingMsRef.current = 0;
      quickTestTimeLimitExpiredRef.current = true;
      setQuickTestRemainingMs(0);
      setQuickTestTimeLimitExpired(true);
    };
    if (remainingMs <= 0) {
      expire();
      return;
    }

    const deadline = Date.now() + remainingMs;
    quickTestDeadlineRef.current = deadline;
    quickTestIntervalRef.current = setInterval(() => {
      const remaining = Math.max(0, deadline - Date.now());
      quickTestRemainingMsRef.current = remaining;
      setQuickTestRemainingMs(remaining);
    }, 250);
    quickTestTimerRef.current = setTimeout(expire, remainingMs);
  }, [clearQuickTestTimer, recordMemoryTrialResult]);

  const startQuickTestTimer = useCallback((card) => {
    clearQuickTestTimer();
    quickTestEligibleRef.current = false;
    quickTestPassedRef.current = false;
    quickTestFirstRepeatObservedRef.current = false;
    quickTestTimeLimitExpiredRef.current = false;
    quickTestRemainingMsRef.current = 0;
    setQuickTestEligible(false);
    setQuickTestPassed(false);
    setQuickTestRemainingMs(0);
    setQuickTestTimeLimitExpired(false);

    const cardType = PracticeSettingsService.getCardType(card);
    if (
      !isFullPractice ||
      !card ||
      cardType === PracticeSettingsService.CARD_TYPES.READING
    ) {
      return;
    }

    const grade = PracticeSettingsService.getAssessmentGrade(card);
    const phase = PracticeSettingsService.getPracticePhase(card);
    const timeLimitMs = getQuickTestTimeLimitMs(card);
    if (
      phase !== PracticeSettingsService.PRACTICE_PHASES.MEMORY_TRIAL ||
      !isQuickTestGradeEligible(grade) ||
      timeLimitMs <= 0
    ) {
      return;
    }

    quickTestEligibleRef.current = true;
    quickTestRemainingMsRef.current = timeLimitMs;
    setQuickTestEligible(true);
    setQuickTestRemainingMs(timeLimitMs);
    resumeQuickTestTimer();
  }, [clearQuickTestTimer, resumeQuickTestTimer, isFullPractice]);

  const recordQuickTestRepeatAttempt = useCallback(() => {
    if (
      !quickTestEligibleRef.current ||
      quickTestFirstRepeatObservedRef.current ||
      quickTestTimeLimitExpiredRef.current ||
      answerViewedRef.current
    ) {
      return false;
    }

    quickTestFirstRepeatObservedRef.current = true;
    quickTestPassedRef.current = true;
    recordMemoryTrialResult(true);
    setQuickTestPassed(true);
    clearQuickTestTimer();
    setQuickTestRemainingMs(0);
    return true;
  }, [clearQuickTestTimer, recordMemoryTrialResult]);

  const resetCurrentCardGradeState = useCallback(() => {
    answerViewedRef.current = false;
    answerOkRef.current = false;
    manualGradeOverrideRef.current = false;
    answerDecisionMadeRef.current = false;
    setHasAnswerDecision(false);
    pendingCardEndGradeRef.current = null;
    answerRevealGradeAppliedRef.current = false;
    messageButtonRevealHandledRef.current = false;
    currentCardDisplayPracticeIncrementsRef.current = 0;
    cardAdvancePendingRef.current = false;
    pendingAdvanceSuppressNotificationRef.current = false;
    advanceInFlightRef.current = false;
    cardAdvanceRequestIdRef.current += 1;
    setMessageButtonCardId(null);
    setIsGradeUpdated(false);
    clearFlashcardRepeatInputLock();
    clearAutoGradeCue();
  }, [clearAutoGradeCue, clearFlashcardRepeatInputLock]);

  const setDisplayedNavigationCard = (card, direction = '') => {
    setSlideDirection(direction);
    setTimeout(() => {
      setReadingResetKey((key) => key + 1);
      setCurrentCard(card);
      setDisplayedNavigationIndex(navigationIndexRef.current);
      setCurrentCardTargetRepeats(getTargetRepeatsForCard(card));
      currentCardSessionRepeatsRef.current = 0;
      setCurrentCardSessionRepeats(0);
      setIsRevealed(false);
      resetCurrentCardGradeState();
      setSlideDirection('');
      advanceInFlightRef.current = false;
    }, direction ? 300 : 0);
  };

  const pushNavigationCard = (card) => {
    if (!card) return;

    const currentIndex = navigationIndexRef.current;
    const nextStack = navigationStackRef.current.slice(0, currentIndex + 1);
    nextStack.push(card);
    navigationStackRef.current = nextStack;
    navigationIndexRef.current = nextStack.length - 1;
    setNavigationStack(nextStack);
    setNavigationIndex(nextStack.length - 1);
  };

  const isViewingHistoryCard = () => (
    navigationIndexRef.current < navigationStackRef.current.length - 1
  );

  const moveNavigationCursor = (nextIndex, direction) => {
    const stack = navigationStackRef.current;
    if (nextIndex < 0 || nextIndex >= stack.length) return false;

    navigationIndexRef.current = nextIndex;
    setNavigationIndex(nextIndex);
    setCardHistory(stack.slice(0, nextIndex));
    setDisplayedNavigationCard(stack[nextIndex], direction);
    return true;
  };

  useEffect(() => { cardPoolRef.current = cardPool; }, [cardPool]);
  useEffect(() => { sessionQueueRef.current = sessionQueue; }, [sessionQueue]);
  useEffect(() => { sessionQueueIndexRef.current = sessionQueueIndex; }, [sessionQueueIndex]);
  useEffect(() => { currentCardRef.current = currentCard; }, [currentCard]);
  useEffect(() => { recentCardIdsRef.current = recentCardIds; }, [recentCardIds]);
  useEffect(() => { sessionResultsRef.current = sessionResults; }, [sessionResults]);
  useEffect(() => { uniqueCardsStudiedRef.current = uniqueCardsStudied; }, [uniqueCardsStudied]);
  useEffect(() => { elapsedTimeRef.current = elapsedTime; }, [elapsedTime]);
  useEffect(() => { totalRepeatCountRef.current = totalRepeatCount; }, [totalRepeatCount]);
  useEffect(() => { totalScoreGainedRef.current = totalScoreGained; }, [totalScoreGained]);
  useEffect(() => { currentCardSessionRepeatsRef.current = currentCardSessionRepeats; }, [currentCardSessionRepeats]);
  useEffect(() => { currentCardSessionScoreGainedRef.current = currentCardSessionScoreGained; }, [currentCardSessionScoreGained]);
  useEffect(() => { currentCardTargetRepeatsRef.current = currentCardTargetRepeats; }, [currentCardTargetRepeats]);
  useEffect(() => { cardHistoryRef.current = cardHistory; }, [cardHistory]);
  useEffect(() => { navigationStackRef.current = navigationStack; }, [navigationStack]);
  useEffect(() => { navigationIndexRef.current = navigationIndex; }, [navigationIndex]);
  useEffect(() => { selectedRatingRef.current = selectedRating; }, [selectedRating]);
  useEffect(() => { npcEncounterRef.current = npcEncounter; }, [npcEncounter]);

  useEffect(() => {
    if (npcEncounter) readingAudio.engine?.stop();
  }, [npcEncounter, readingAudio.engine]);

  useEffect(() => {
    if (importPaused && ['playing', 'loading', 'gap'].includes(readingAudio.state.status)) readingAudio.engine?.pause();
  }, [importPaused, readingAudio.engine, readingAudio.state.status]);

  useEffect(() => {
    setIsTtsAnswerVisible(reverseMode);
  }, [currentCard?.id, currentCard?._id, reverseMode]);

  useEffect(() => {
    setReadingProgressPercent(0);
  }, [currentCard?.id, currentCard?._id, readingResetKey]);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = Promise.resolve(
      typeof apiClient.getCurrentUserSummary === 'function'
        ? apiClient.getCurrentUserSummary()
        : null
    );

    loadSummary
      .then((profile) => {
        if (cancelled) return;
        const summary = profile?.summary || {};
        const explicitLevel = Number(summary.level);
        if (Number.isInteger(explicitLevel) && explicitLevel >= 1) {
          setPlayerLevel(explicitLevel);
          return;
        }

        const exp = Math.max(0, Math.floor(Number(summary.exp ?? summary.totalScoreGained) || 0));
        setPlayerLevel(Math.floor(exp / 5000) + 1);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Failed to load practice user level:', err);
          setPlayerLevel(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const maybeStartNpcEncounter = async (isCurrentAdvance = () => isMountedRef.current) => {
    if (
      !isCurrentAdvance() ||
      !isFullPractice ||
      npcEncounterCheckedRef.current ||
      npcEncounterRef.current ||
      activeCelebrationPopupRef.current ||
      celebrationTransitionRef.current ||
      queuedCelebrationPopupsRef.current.length > 0 ||
      totalScoreGainedRef.current <= NPC_ENCOUNTER_SCORE_THRESHOLD
    ) {
      return false;
    }

    npcEncounterCheckedRef.current = true;

    let allCards = cardPoolRef.current;
    try {
      allCards = await apiClient.getAllFlashCards({ status: 'graduated' });
    } catch (err) {
      console.warn('Failed to load cards for NPC encounter:', err);
    }

    if (!isCurrentAdvance()) return false;
    const eligibleCards = getNpcEncounterEligibleCards(allCards, new Date());
    if (!hasEnoughNpcEncounterCards(eligibleCards)) {
      return false;
    }

    try {
      const sceneResult = await apiClient.getRandomScene({
        trigger: 'Encounter.before',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        seed: `${authService.getCurrentUserId() || 'anonymous'}:${Date.now()}:npc-encounter`,
      });
      const scene = sceneResult?.scene || null;
      if (!scene || !isCurrentAdvance()) return false;

      const nextEncounter = {
        phase: 'beforeScene',
        beforeScene: scene,
        resultScene: null,
        cards: eligibleCards.slice(0, NPC_ENCOUNTER_CARD_COUNT),
        result: null,
      };
      npcEncounterRef.current = nextEncounter;
      npcJudgedCardIdsRef.current.clear();
      setNpcEncounter(nextEncounter);
      return true;
    } catch (err) {
      console.warn('Failed to load NPC encounter scene:', err);
      return false;
    }
  };

  const showAdvanceAchievement = (achievement, onDone) => {
    enqueueCelebrationPopup({
      ...achievement,
      kind: 'advanceAchievement',
      onClose: onDone,
    });
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      cardAdvanceRequestIdRef.current += 1;
      pendingCardAdvanceContinuationRef.current = null;
      pendingAdvanceAfterEncounterRef.current = null;
      queuedCelebrationPopupsRef.current = [];
      celebrationTransitionRef.current = false;
      if (nextCelebrationTimerRef.current) {
        clearTimeout(nextCelebrationTimerRef.current);
        nextCelebrationTimerRef.current = null;
      }
      if (targetAdvanceTimerRef.current) {
        clearTimeout(targetAdvanceTimerRef.current);
      }
      if (cardDepartureWaitRef.current) {
        clearTimeout(cardDepartureWaitRef.current.timer);
        cardDepartureWaitRef.current.resolve(); cardDepartureWaitRef.current = null;
      }
      if (sessionEndTimerRef.current) {
        clearTimeout(sessionEndTimerRef.current);
      }
      if (cueTimerRef.current) {
        clearTimeout(cueTimerRef.current);
      }
      if (flashcardRepeatInputLockTimerRef.current) {
        clearTimeout(flashcardRepeatInputLockTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (arePracticeTimersPaused) return undefined;
    const startedAt = Date.now();
    const previousElapsedMs = sessionElapsedMsRef.current;
    const updateElapsed = () => {
      const elapsedMs = previousElapsedMs + Date.now() - startedAt;
      sessionElapsedMsRef.current = elapsedMs;
      elapsedTimeRef.current = Math.floor(elapsedMs / 1000);
      if (isMountedRef.current) setElapsedTime(elapsedTimeRef.current);
      return elapsedMs;
    };
    const tick = () => {
      const elapsedMs = updateElapsed();
      timer = setTimeout(tick, 1000 - (elapsedMs % 1000));
    };
    // Preserve the partial second when a popup interrupts the session clock.
    let timer = setTimeout(tick, 1000 - (previousElapsedMs % 1000));
    return () => {
      clearTimeout(timer);
      updateElapsed();
    };
  }, [arePracticeTimersPaused]);

  useEffect(() => {
    const checkpointSeconds = getPendingPracticeCheckpoint(
      elapsedTime,
      lastSavedCheckpointRef.current
    );
    if (!checkpointSeconds || checkpointSavePromiseRef.current || !persistCheckpointRef.current) return;
    persistCheckpointRef.current(checkpointSeconds);
  }, [elapsedTime]);

  useEffect(() => {
    if (
      elapsedTime < SESSION_TIME_LIMIT_SECONDS ||
      sessionLimitReachedRef.current
    ) {
      return;
    }

    sessionLimitReachedRef.current = true;
  }, [elapsedTime]);

  useEffect(() => {
    if (!isEditModalOpen) return undefined;

    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyWidth = document.body.style.width;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.width = originalBodyWidth;
      try {
        if (window.navigator?.userAgent?.includes('jsdom')) return;
        window.scrollTo(0, scrollY);
      } catch {
        // jsdom does not implement scrollTo; browsers do.
      }
    };
  }, [isEditModalOpen]);

  useEffect(() => {
    if (currentCard) {
      const currentCardId = getCardId(currentCard);
      if (currentCardId !== currentCardDisplayIdRef.current) {
        currentCardDisplayTokenRef.current += 1;
        currentCardDisplayIdRef.current = currentCardId;
        currentCardStartedUngradedRef.current = PracticeSettingsService.getAssessmentGrade(currentCard) === 0;
        resetCurrentCardGradeState();
        startQuickTestTimer(currentCard);
        setCurrentCardSessionScoreGained(0);
        setCurrentScoreBonusEvent({ id: scoreBonusEventIdRef.current, bonuses: [] });
        currentCardDisplayDaysSinceRef.current = getDaysSince(currentCard);
        loadHintMessagesForCard(currentCard);
        setDisplayedCardStats({
          practiceCount: currentCard.practiceCount || 0,
          score: currentCard.score || 0,
          daysSince: currentCardDisplayDaysSinceRef.current,
        });
      }
      if (currentCardId) {
        seenCycleIdsRef.current.add(currentCardId);
      }
      setSelectedRating(PracticeSettingsService.getAssessmentGrade(currentCard) || 1);

    }
  }, [currentCard, isQuickMode, loadHintMessagesForCard, resetCurrentCardGradeState, startQuickTestTimer]);

  const sentenceCueCardId = getCardId(currentCard);
  const sentenceCueCardType = PracticeSettingsService.getCardType(currentCard);
  const sentenceCueDisplayCard = PracticeSettingsService.getPracticeDisplayCard(currentCard);
  const sentenceCueHasPre = Boolean(sentenceCueDisplayCard?.contextPre);
  const sentenceCueHasAfter = Boolean(sentenceCueDisplayCard?.contextAfter);

  useEffect(() => {
    const supportsEntranceCues = sentenceCueCardType === PracticeSettingsService.CARD_TYPES.NORMAL ||
      sentenceCueCardType === PracticeSettingsService.CARD_TYPES.SIMPLE;
    if (!sentenceCueCardId || !supportsEntranceCues) {
      return undefined;
    }

    const cues = sentenceCueHasPre
      ? [
        { effect: 'sentencePre', delay: 0 },
        { effect: 'sentenceCard', delay: 500 },
        ...(sentenceCueHasAfter ? [{ effect: 'sentenceAfter', delay: 680 }] : []),
      ]
      : [
        { effect: 'sentenceCard', delay: 0 },
        ...(sentenceCueHasAfter ? [{ effect: 'sentenceAfter', delay: 500 }] : []),
      ];

    const timers = cues.map(({ effect, delay }) => setTimeout(() => {
      if (currentCardDisplayIdRef.current !== sentenceCueCardId) return;
      void playPracticeSound(effect);
    }, delay));

    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [sentenceCueCardId, sentenceCueCardType, sentenceCueHasAfter, sentenceCueHasPre]);

  useEffect(() => {
    if (arePracticeTimersPaused || !currentCard) {
      clearQuickTestTimer();
      if (quickTestEligibleRef.current && !quickTestPassedRef.current) {
        setQuickTestRemainingMs(quickTestRemainingMsRef.current);
      }
      return;
    }
    resumeQuickTestTimer();
  }, [arePracticeTimersPaused, clearQuickTestTimer, currentCard, resumeQuickTestTimer]);

  useEffect(() => () => {
    clearQuickTestTimer();
  }, [clearQuickTestTimer]);

  useEffect(() => {
    let cancelled = false;
    const localNames = collectNoteNamesFromCards(flashCards);

    setNoteNameById((prev) => ({ ...prev, ...localNames }));

    if (isQuickMode) {
      let objectUrl;
      const controller = new AbortController();
      const source = settings.quickSource;
      if (source) {
        const request = source.type === 'preset' ? apiClient.fetchPresetLibraryCover(source.id, { signal: controller.signal }) : apiClient.fetchLibraryMarketCover(source.id, { signal: controller.signal });
        request.then(blob => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setNoteCoverById(Object.fromEntries(flashCards.map(card => [card.noteId, objectUrl])));
        }).catch(() => {});
      }
      return () => { cancelled = true; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }
    apiClient.getAllNotes().then((loadedNotes) => {
      if (cancelled) return;

      const metadataNames = collectNoteNamesFromMetadata(loadedNotes);
      const metadataCovers = collectNoteCoversFromMetadata(loadedNotes);

      if (Object.keys(metadataNames).length > 0) {
        setNoteNameById((prev) => ({ ...prev, ...metadataNames }));
      }
      if (Object.keys(metadataCovers).length > 0) {
        setNoteCoverById((prev) => ({ ...prev, ...metadataCovers }));
      }
    }).catch(() => {
      // The direct card data is still usable if lookup fails.
    });

    return () => {
      cancelled = true;
    };
  }, [flashCards, isQuickMode, settings.quickSource]);

  const hasNoPracticeCard = !flashCards || flashCards.length === 0 || !currentCard;
  const hasBufferedPracticeProgress =
    totalRepeatCount > 0 ||
    totalScoreGained !== 0 ||
    uniqueCardsStudied.size > 0 ||
    pendingCardProgressRef.current.size > 0 ||
    sessionResults.length > 0;

  useEffect(() => {
    if (
      !hasNoPracticeCard ||
      !hasBufferedPracticeProgress ||
      isProcessing ||
      error?.sessionEndFailed ||
      activeCelebrationPopupRef.current ||
      queueExhaustedCelebrationShownRef.current
    ) {
      return;
    }

    void performEndSessionRef.current?.({ endReason: 'queueExhausted' });
  }, [hasNoPracticeCard, hasBufferedPracticeProgress, isProcessing, error?.sessionEndFailed]);

  const resolveQueuedCard = (queuedCard) => {
    const queuedId = getCardId(queuedCard);
    if (!queuedId) return queuedCard;

    const currentCardData = cardPoolRef.current.find((card) => getCardId(card) === queuedId);
    if (!currentCardData) return null;

    return {
      ...currentCardData,
      practicePhase: queuedCard.practicePhase,
    };
  };

  const getNextQueuedCard = () => {
    const queue = sessionQueueRef.current || [];

    for (let index = sessionQueueIndexRef.current + 1; index < queue.length; index += 1) {
      const card = resolveQueuedCard(queue[index]);
      if (card) {
        return { card, index };
      }
    }

    return null;
  };

  const getProjectedScore = (card, optimisticScoreDelta = 0) =>
    PracticeSettingsService.getCardScore(card) + optimisticScoreDelta;

  const getEffectiveScoreLimit = (card) => Math.min(
    PracticeSettingsService.getCardScoreLimit(card),
    maxScore
  );

  const getLimitedScoreDelta = (card, requestedScoreDelta = 0) => {
    if (isCompletedReviewMode) {
      return Math.max(0, Number(requestedScoreDelta) || 0);
    }

    const currentScore = PracticeSettingsService.getCardScore(card);
    const remainingScore = Math.max(0, getEffectiveScoreLimit(card) - currentScore);
    return Math.min(Math.max(0, Number(requestedScoreDelta) || 0), remainingScore);
  };

  const hasReachedScoreLimit = (card, optimisticScoreDelta = 0) =>
    getProjectedScore(card, optimisticScoreDelta) >= getEffectiveScoreLimit(card);

  const isAutoGradeLocked = (card) => {
    if (!card) return true;
    if (isCompletedReviewMode) return true;
    return PracticeSettingsService.isGraduated(card) || hasReachedScoreLimit(card);
  };

  const getAnswerRevealAutoGrade = (card) => {
    return calculateAnswerRevealAutoGrade(PracticeSettingsService.getAssessmentGrade(card));
  };

  const applyAnswerRevealGrade = async () => {
    const card = currentCardRef.current;
    if (!card || answerRevealGradeAppliedRef.current || isAutoGradeLocked(card)) {
      return false;
    }

    disableQuickTestTimerForCurrentCard();
    answerRevealGradeAppliedRef.current = true;
    answerViewedRef.current = true;
    answerOkRef.current = false;
    const revealGrade = getAnswerRevealAutoGrade(card);
    pendingCardEndGradeRef.current = revealGrade;

    if (PracticeSettingsService.getAssessmentGrade(card) === revealGrade) {
      applyDisplayedGrade(revealGrade);
      return true;
    }

    try {
      await persistGrade(revealGrade, {
        force: manualGradeOverrideRef.current,
        cueMessage: `Auto: grade ${revealGrade}`,
      });
      return true;
    } catch (error) {
      answerRevealGradeAppliedRef.current = false;
      throw error;
    }
  };

  const handleOpenEditModal = () => {
    if (isQuickMode) return;
    if (achievementActiveRef.current || isTtsInteractionLockedRef.current) return;

    const nextEditingCard = currentCardRef.current;
    editingCardRef.current = nextEditingCard;
    setEditingCard(nextEditingCard);


    if (!isViewingHistoryCard() && !isCompletedReviewMode) {
      void applyAnswerRevealGrade();
    }

    setIsEditModalOpen(true);
  };

  const handleNpcEncounterLevelUp = (card) => {
    if (isProcessing || reviewCard || isLevelUpModalOpen || isConfirmDialogOpen || npcEncounterRef.current?.phase !== 'battle') return;
    const encounterCard = npcEncounterRef.current.cards.find((item) => getCardId(item) === getCardId(card));
    if (!PracticeSettingsService.isCardUpgradeable(encounterCard, { allowGraduated: true })) return;
    setReviewCard(encounterCard);
  };

  const getDaysSince = (card) => {
    const lastTimestamp = card?.lastPracticedAt ?? null;
    if (!lastTimestamp) return null;
    const last = new Date(lastTimestamp);
    const now = new Date();
    last.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    return Math.floor((now - last) / 86400000);
  };

  const recordPracticedCardAdvance = () => {
    const card = currentCardRef.current;
    const cardId = getCardId(card);
    if (!cardId) return null;

    const displayToken = currentCardDisplayTokenRef.current;
    if (!practicedDisplayTokensRef.current.has(displayToken)) return null;
    if (lastCountedPracticeDisplayTokenRef.current === displayToken) return null;

    lastCountedPracticeDisplayTokenRef.current = displayToken;
    practicedCardCountRef.current += 1;

    return practicedCardCountRef.current;
  };

  const getAdvanceAchievement = (practicedCount) => {
    const milestoneMinutes =
      Math.floor(elapsedTimeRef.current / TIME_ACHIEVEMENT_INTERVAL_SECONDS) * 10;

    if (
      milestoneMinutes > 0 &&
      milestoneMinutes > lastTimeAchievementMinutesRef.current
    ) {
      lastTimeAchievementMinutesRef.current = milestoneMinutes;
      return {
        message: t('practiceUi.completion.minutes', undefined, { count: milestoneMinutes }),
        ...getAchievementSpriteConfig(clockRingingSprite, 16),
      };
    }

    return {
      message: t('practiceUi.completion.cards', undefined, { count: practicedCount }),
      ...getRandomNextRepeatSprite(),
    };
  };

  const finishSessionAfterAchievement = (achievement, endReason = null) => {
    if (!achievement) {
      void performEndSession({ endReason });
      return;
    }

    showAdvanceAchievement(achievement, () => {
      void performEndSession({ endReason });
    });
  };

  const finalizeCardGradeForAdvance = async () => {
    if (isViewingHistoryCard()) {
      return false;
    }

    const card = currentCardRef.current;
    if (!card || manualGradeOverrideRef.current || isAutoGradeLocked(card)) {
      return false;
    }
    const cardType = PracticeSettingsService.getCardType(card);
    if (
      cardType === PracticeSettingsService.CARD_TYPES.READING
    ) {
      return false;
    }

    const displayToken = currentCardDisplayTokenRef.current;
    const practicedThisDisplay = practicedDisplayTokensRef.current.has(displayToken);
    const hasStudyActivity = practicedThisDisplay || answerViewedRef.current || Boolean(pendingCardEndGradeRef.current);
    if (!hasStudyActivity) {
      return false;
    }

    const endedWithoutViewingAnswer = !answerViewedRef.current;
    const gradeBeforeAuto = PracticeSettingsService.getAssessmentGrade(card);

    let finalGrade = pendingCardEndGradeRef.current;
    if (!finalGrade) {
      if (answerViewedRef.current && !answerOkRef.current) {
        finalGrade = getAnswerRevealAutoGrade(card);
      } else if (endedWithoutViewingAnswer) {
        finalGrade = calculateNoFlipCompletionAutoGrade(
          gradeBeforeAuto,
          quickTestPassedRef.current
        );
      } else {
        return false;
      }
    }

    const result = await persistGrade(finalGrade, {
      cueMessage: `Auto: grade ${finalGrade}`,
    });

    pendingCardEndGradeRef.current = null;
    return Boolean(result?.finished);
  };

  const shouldEndAtNextAdvance = () => {
    if (sessionLimitEndingRef.current) return false;
    if (sessionLimitReachedRef.current || elapsedTimeRef.current >= SESSION_TIME_LIMIT_SECONDS) {
      sessionLimitReachedRef.current = true;
      sessionLimitEndingRef.current = true;
      return true;
    }
    return false;
  };

  const advanceToNextCard = ({ suppressNotification = false, userInitiated = false } = {}) => {
    void unlockPracticeAudio();
    clearMessageButton();
    if (isTtsInteractionLockedRef.current) return false;
    if (
      activeCelebrationPopupRef.current ||
      celebrationTransitionRef.current ||
      queuedCelebrationPopupsRef.current.length > 0 ||
      boardBreakPopupOpenRef.current ||
      achievementActiveRef.current ||
      advanceInFlightRef.current ||
      npcEncounterRef.current
    ) {
      return false;
    }

    const resolvedSuppressNotification =
      suppressNotification || pendingAdvanceSuppressNotificationRef.current;
    cardAdvancePendingRef.current = false;
    pendingAdvanceSuppressNotificationRef.current = false;

    if (isViewingHistoryCard()) {
      moveNavigationCursor(navigationIndexRef.current + 1, 'left');
      return true;
    }

    advanceInFlightRef.current = true;
    const advanceRequestId = ++cardAdvanceRequestIdRef.current;
    const isCurrentAdvance = () => isMountedRef.current && cardAdvanceRequestIdRef.current === advanceRequestId;
    const departingPracticePhase = PracticeSettingsService.getPracticePhase(currentCardRef.current);

    if (targetAdvanceTimerRef.current) {
      clearTimeout(targetAdvanceTimerRef.current);
      targetAdvanceTimerRef.current = null;
    }

    const continueAdvance = () => {
      if (departingPracticePhase === PracticeSettingsService.PRACTICE_PHASES.MEMORY_TRIAL) recordMemoryTrialResult(false);
      const card = currentCardRef.current;
      const recent = recentCardIdsRef.current;
      const cardId = getCardId(card);
      const practicedCount = recordPracticedCardAdvance();
      const achievement = !resolvedSuppressNotification && practicedCount
        ? getAdvanceAchievement(practicedCount)
        : null;

      const newRecentIds = cardId ? [...recent, cardId].slice(-3) : recent.slice(-3);
      const queuedNext = getNextQueuedCard();
      if (!queuedNext?.card) {
        const finishQueue = () => {
          advanceInFlightRef.current = false;
          void performEndSession({ endReason: 'queueExhausted' });
        };
        const finishAfterPhaseEffect = () => {
          enqueueCelebrationPopup({
            phaseTransition: 'memoryTrialComplete',
            title: t('practicePhases.memoryTrial.completeTitle'),
            message: getMemoryTrialCompletionMessage('practicePhases.memoryTrial.completeWithoutCore'),
            iconSrc: fluencyTestEndImage,
            iconAlt: t('practicePhases.memoryTrial.endImageAlt'),
            iconLayout: 'speech',
            onClose: finishQueue,
          });
        };

        if (departingPracticePhase === PracticeSettingsService.PRACTICE_PHASES.MEMORY_TRIAL) {
          if (achievement) {
            showAdvanceAchievement(achievement, finishAfterPhaseEffect);
          } else {
            finishAfterPhaseEffect();
          }
          return;
        }

        advanceInFlightRef.current = false;
        finishSessionAfterAchievement(achievement, 'queueExhausted');
        return;
      }

      const next = queuedNext.card;
      const nextPracticePhase = PracticeSettingsService.getPracticePhase(next);

      const completeAdvance = () => {
        if (!isCurrentAdvance()) return;
        sessionQueueIndexRef.current = queuedNext.index;
        setSessionQueueIndex(queuedNext.index);
        pushNavigationCard(next);
        setCardHistory(navigationStackRef.current.slice(0, navigationIndexRef.current));
        const displayNext = () => {
          if (!isCurrentAdvance()) return;
          setRecentCardIds(newRecentIds);
          setReadingResetKey((key) => key + 1);
          setCurrentCard(next);
          setDisplayedNavigationIndex(navigationIndexRef.current);
          setCurrentCardTargetRepeats(getTargetRepeatsForCard(next));
          currentCardSessionRepeatsRef.current = 0;
          setCurrentCardSessionRepeats(0);
          setIsRevealed(false);
          resetCurrentCardGradeState();
          setSlideDirection('');
          advanceInFlightRef.current = false;
        };
        if (departedDisplayTokenRef.current === currentCardDisplayTokenRef.current) displayNext();
        else { setSlideDirection('left'); setTimeout(displayNext, 300); }
      };

      const advanceAfterCelebrations = () => {
        pendingCardAdvanceContinuationRef.current = () => {
          if (!isCurrentAdvance()) return;
          if (departingPracticePhase !== PracticeSettingsService.PRACTICE_PHASES.CORE_TRAINING) {
            completeAdvance();
            return;
          }
          // Popups (including onClose's transition turn) are fully drained now.
          // Resume directly into the next card after the NPC, not another celebration.
          maybeStartNpcEncounter(isCurrentAdvance).then((started) => {
            if (!isCurrentAdvance()) return;
            if (started) {
              pendingAdvanceAfterEncounterRef.current = completeAdvance;
              advanceInFlightRef.current = false;
            } else completeAdvance();
          }).catch((err) => {
            console.warn('Failed to start NPC encounter:', err);
            completeAdvance();
          });
        };
        showNextCelebrationPopupRef.current?.();
      };

      const advanceAfterPhaseEffect = () => {
        const isLeavingMemoryTrial =
          departingPracticePhase === PracticeSettingsService.PRACTICE_PHASES.MEMORY_TRIAL &&
          nextPracticePhase === PracticeSettingsService.PRACTICE_PHASES.CORE_TRAINING;

        if (isLeavingMemoryTrial) {
          enqueueCelebrationPopup({
            phaseTransition: 'memoryTrialComplete',
            title: t('practicePhases.memoryTrial.completeTitle'),
            message: getMemoryTrialCompletionMessage('practicePhases.memoryTrial.completeToCore'),
            iconSrc: fluencyTestEndImage,
            iconAlt: t('practicePhases.memoryTrial.endImageAlt'),
            iconLayout: 'speech',
            onClose: advanceAfterCelebrations,
          });
          return;
        }

        advanceAfterCelebrations();
      };

      if (achievement) {
        showAdvanceAchievement(achievement, advanceAfterPhaseEffect);
        return;
      }

      advanceAfterPhaseEffect();
    };

    const finalizeAfterDeparture = async () => {
      if (userInitiated && PracticeSettingsService.getCardType(currentCardRef.current) !== PracticeSettingsService.CARD_TYPES.READING &&
        departedDisplayTokenRef.current !== currentCardDisplayTokenRef.current) {
        setSlideDirection('left');
        clearQuickTestTimer();
        await new Promise(resolve => {
          cardDepartureWaitRef.current = { resolve, timer: setTimeout(() => {
            cardDepartureWaitRef.current = null; resolve();
          }, window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 80 : 300) };
        });
        if (!isCurrentAdvance()) return false;
        departedDisplayTokenRef.current = currentCardDisplayTokenRef.current;
      }
      return finalizeCardGradeForAdvance();
    };
    finalizeAfterDeparture()
      .then((finished) => {
        if (!isCurrentAdvance()) return;
        if (finished) {
          advanceInFlightRef.current = false;
          cardAdvancePendingRef.current = true;
          if (!boardBreakPopupOpenRef.current && !activeCelebrationPopupRef.current) {
            scheduleAdvanceAfterTarget();
          }
          return;
        }

        if (shouldEndAtNextAdvance()) {
          advanceInFlightRef.current = false;
          void performEndSession();
          return;
        }

        continueAdvance();
      })
      .catch(() => {
        if (!isCurrentAdvance()) return;
        advanceInFlightRef.current = false;
        departedDisplayTokenRef.current = null;
        if (isMountedRef.current) setSlideDirection('');
      });

    return true;
  };

  const goToPrevCard = () => {
    void unlockPracticeAudio();
    clearMessageButton();
    if (isTtsInteractionLockedRef.current) return false;
    if (achievementActiveRef.current || boardBreakPopupOpenRef.current || npcEncounterRef.current) return false;

    if (targetAdvanceTimerRef.current) {
      clearTimeout(targetAdvanceTimerRef.current);
      targetAdvanceTimerRef.current = null;
    }

    return moveNavigationCursor(navigationIndexRef.current - 1, 'right');
  };

  const scheduleAdvanceAfterTarget = (delay = 0) => {
    cardAdvancePendingRef.current = true;
    if (targetAdvanceTimerRef.current) return;

    targetAdvanceTimerRef.current = setTimeout(() => {
      targetAdvanceTimerRef.current = null;
      advanceToNextCard({
        suppressNotification: pendingAdvanceSuppressNotificationRef.current,
      });
    }, delay);
  };

  const triggerBoardBreak = (card) => {
    const cardId = getCardId(card);
    if (!cardId || boardBreakShownIdsRef.current.has(cardId)) {
      return false;
    }

    finishedCardIdsRef.current.add(cardId);
    boardBreakShownIdsRef.current.add(cardId);

    boardBreakPopupOpenRef.current = true;
    const graduationType = PracticeSettingsService.getCardGraduationType(card) ||
      PracticeSettingsService.GRADUATION_TYPES.MANUAL;
    const message = GRADUATION_CELEBRATION_MESSAGES[graduationType] ||
      GRADUATION_CELEBRATION_MESSAGES[PracticeSettingsService.GRADUATION_TYPES.MANUAL];
    const popup = {
      cardId,
      title: t(message.title),
      message: t(message.message),
      animationSpriteSrc: completionCelebrationSprite,
      animationFrameCount: 16,
      animationFrameSize: 128,
      animationDurationMs: 1000,
      onClose: handleBoardBreakPopupClose,
    };
    setBoardBreakPopup(popup);
    enqueueCelebrationPopup({
      kind: 'boardBreak',
      ...popup,
    });

    return true;
  };

  const handleBoardBreakPopupClose = () => {
    boardBreakPopupOpenRef.current = false;
    setBoardBreakPopup(null);
    cardAdvancePendingRef.current = true;
    pendingAdvanceSuppressNotificationRef.current = true;
  };

  const mergeCardUpdate = (existingCard, updatedCard) => {
    if (!existingCard) return updatedCard;
    return {
      ...existingCard,
      ...updatedCard,
    };
  };

  const mergeReadingChildUpdate = (existingCard, updatedCard) => {
    if (!Array.isArray(existingCard?.readingChildren)) return null;
    const childId = getCardId(updatedCard);
    if (!existingCard.readingChildren.some((child) => getCardId(child) === childId)) return null;
    const readingChildren = existingCard.readingChildren.map((child) => (
      getCardId(child) === childId ? mergeCardUpdate(child, updatedCard) : child
    ));
    const completed = readingChildren.length > 0 && readingChildren.every(PracticeSettingsService.isCardCompleted);
    const averageChildValue = (selector) => readingChildren.length > 0
      ? Math.round(readingChildren.reduce((total, child) => total + Number(selector(child) || 0), 0) / readingChildren.length)
      : 0;
    return {
      ...existingCard,
      readingChildren,
      score: averageChildValue(PracticeSettingsService.getCardScore),
      practiceCount: averageChildValue(PracticeSettingsService.getCardPracticeCount),
      assessmentGrade: null,
      completedAt: completed
        ? readingChildren.map((child) => child.completedAt).filter(Boolean).sort().at(-1) || existingCard.completedAt || null
        : null,
    };
  };

  const mergeCardOrReadingChild = (existingCard, updatedCard) => {
    if (getCardId(existingCard) === getCardId(updatedCard)) return mergeCardUpdate(existingCard, updatedCard);
    return mergeReadingChildUpdate(existingCard, updatedCard) || existingCard;
  };

  const containsCardOrReadingChild = (container, cardId) => (
    getCardId(container) === cardId ||
    container?.readingChildren?.some((child) => getCardId(child) === cardId)
  );

  const syncUpdatedCard = (updatedCard) => {
    if (!updatedCard || !isMountedRef.current) return [];

    const updatedCardId = getCardId(updatedCard);
    let mergedCurrentCard = updatedCard;
    const updatedPool = cardPoolRef.current.map((c) => {
      if (!containsCardOrReadingChild(c, updatedCardId)) {
        return c;
      }

      const merged = mergeCardOrReadingChild(c, updatedCard);
      if (containsCardOrReadingChild(currentCardRef.current, updatedCardId)) {
        mergedCurrentCard = merged;
      }
      return merged;
    });
    cardPoolRef.current = updatedPool;

    const updatedNavigationStack = navigationStackRef.current.map((c) => {
      if (!containsCardOrReadingChild(c, updatedCardId)) {
        return c;
      }

      return mergeCardOrReadingChild(c, updatedCard);
    });
    navigationStackRef.current = updatedNavigationStack;

    const updatedSessionQueue = sessionQueueRef.current.map((c) => {
      if (!containsCardOrReadingChild(c, updatedCardId)) {
        return c;
      }

      return mergeCardOrReadingChild(c, updatedCard);
    });
    sessionQueueRef.current = updatedSessionQueue;

    const currentEncounter = npcEncounterRef.current;
    if (currentEncounter?.cards?.some((card) => containsCardOrReadingChild(card, updatedCardId))) {
      const nextEncounter = {
        ...currentEncounter,
        cards: currentEncounter.cards.map((card) => mergeCardOrReadingChild(card, updatedCard)),
      };
      npcEncounterRef.current = nextEncounter;
      setNpcEncounter(nextEncounter);
    }

    const isCurrentCard = containsCardOrReadingChild(currentCardRef.current, updatedCardId);
    if (isCurrentCard) {
      currentCardRef.current = mergedCurrentCard;
      setDisplayedCardStats({
        practiceCount: mergedCurrentCard.practiceCount || 0,
        score: mergedCurrentCard.score || 0,
        daysSince: currentCardDisplayDaysSinceRef.current,
      });
    }

    setCardPool(updatedPool);
    setNavigationStack(updatedNavigationStack);
    setSessionQueue(updatedSessionQueue);
    setCurrentCard((prev) => {
      if (containsCardOrReadingChild(prev, updatedCardId)) {
        return mergeCardOrReadingChild(prev, updatedCard);
      }

      return prev;
    });
    if (!isQuickMode && onFlashCardsUpdate) onFlashCardsUpdate(updatedPool);

    return updatedPool;
  };

  const mergePendingProgress = (older, newer) => ({
    ...older,
    ...newer,
    id: newer?.id || older?.id,
    practiceCountDelta: (older?.practiceCountDelta || 0) + (newer?.practiceCountDelta || 0),
    scoreDelta: (older?.scoreDelta || 0) + (newer?.scoreDelta || 0),
    practiceCountFloor: Math.max(
      Number(older?.practiceCountFloor) || 0,
      Number(newer?.practiceCountFloor) || 0
    ) || null,
    testIndex: newer?.testIndex ?? older?.testIndex ?? null,
    graduationType: newer?.graduationType || older?.graduationType || null,
    completedAt: newer?.completedAt || older?.completedAt || null,
    lastPracticedAt: newer?.lastPracticedAt || older?.lastPracticedAt || null,
    progressedAt: older?.progressedAt || newer?.progressedAt || null,
    assessments: [...(older?.assessments || []), ...(newer?.assessments || [])],
  });

  const flushPendingPracticeProgress = async () => {
    if (isQuickMode) return [];
    if (progressFlushPromiseRef.current) {
      await progressFlushPromiseRef.current;
    }

    const pendingUpdates = Array.from(pendingCardProgressRef.current.values())
      .filter((update) => (
        (update.practiceCountDelta || 0) > 0 ||
        (update.scoreDelta || 0) > 0 ||
        (update.testIndex !== null && update.testIndex !== undefined) ||
        PracticeSettingsService.normalizeGraduationType(update.graduationType) ||
        update.completedAt ||
        (update.assessments?.length || 0) > 0 ||
        update.lastPracticedAt
      ));

    if (pendingUpdates.length === 0) {
      return [];
    }

    pendingUpdates.forEach((update) => {
      if (pendingCardProgressRef.current.get(update.id) === update) {
        pendingCardProgressRef.current.delete(update.id);
      }
    });
    syncPendingProgressCount();

    const flushPromise = (async () => {
      try {
        const progressResponse = await apiClient.savePracticeProgressBatch({
          updates: pendingUpdates,
          practiceStartId,
        });

        const updatedCards = progressResponse?.updatedCards || [];
        updatedCards.forEach((updatedCard) => {
          syncUpdatedCard(updatedCard);
        });
        return updatedCards;
      } catch (error) {
        pendingUpdates.forEach((update) => {
          const newer = pendingCardProgressRef.current.get(update.id);
          pendingCardProgressRef.current.set(
            update.id,
            newer ? mergePendingProgress(update, newer) : update
          );
        });
        syncPendingProgressCount();
        throw error;
      }
    })();

    progressFlushPromiseRef.current = flushPromise;
    try {
      return await flushPromise;
    } finally {
      if (progressFlushPromiseRef.current === flushPromise) {
        progressFlushPromiseRef.current = null;
      }
    }
  };

  const savePracticeSessionSnapshot = async ({ isComplete = false } = {}) => {
    if (isWritingReview) return;
    const snapshotElapsedTime = Math.max(1, elapsedTimeRef.current);
    return apiClient.savePracticeSession({
      sessionId: practiceSessionRecordIdRef.current,
      startTime: sessionStartedAtRef.current,
      endTime: new Date().toISOString(),
      duration: snapshotElapsedTime,
      cardsStudied: isQuickMode ? 0 : uniqueCardsStudiedRef.current.size,
      repeatCount: isQuickMode ? 0 : totalRepeatCountRef.current,
      scoreGained: totalScoreGainedRef.current,
      practiceStartId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      mode: isQuickMode ? 'quick' : isCompletedReviewMode ? PRACTICE_MODE_COMPLETED_REVIEW : 'normal',
      isComplete,
    });
  };

  persistCheckpointRef.current = (checkpointSeconds) => {
    const checkpointPromise = (async () => {
      try {
        await flushPendingPracticeProgress();
        await savePracticeSessionSnapshot({ isComplete: false });
        lastSavedCheckpointRef.current = checkpointSeconds;
      } catch (error) {
        console.warn(`Failed to save ${checkpointSeconds / 60}-minute practice checkpoint:`, error);
      }
    })();
    checkpointSavePromiseRef.current = checkpointPromise;
    checkpointPromise.finally(() => {
      if (checkpointSavePromiseRef.current === checkpointPromise) {
        checkpointSavePromiseRef.current = null;
      }
    });
    return checkpointPromise;
  };

  const buildLocallyPracticedCard = (card, practicedAt, scoreDelta = 0, updateLastPracticedAt = true) => ({
    ...card,
    practiceCount: PracticeSettingsService.getCardPracticeCount(card) + 1,
    score: PracticeSettingsService.getCardScore(card) + getLimitedScoreDelta(card, scoreDelta),
    lastPracticedAt: updateLastPracticedAt ? practicedAt : card.lastPracticedAt,
  });

  const buildLocallyEvaluatedCard = (card, grade, timestamp, graduationType = null, testIndexOverride = null) => ({
    ...card,
    practiceCount: grade === 4
      ? Math.max(PracticeSettingsService.getCardPracticeCount(card), GRADE_FOUR_MIN_REPEAT_COUNT)
      : PracticeSettingsService.getCardPracticeCount(card),
    testIndex: testIndexOverride === null || testIndexOverride === undefined
      ? PracticeSettingsService.calculateNextTestIndex(card, grade)
      : testIndexOverride,
    graduationType: PracticeSettingsService.resolveGraduationType(card, grade, graduationType),
    assessmentGrade: grade,
  });

  const getPracticeScoreBreakdown = (card) => {
    const cardId = getCardId(card);
    return calculatePracticeScoreBreakdown({
      practiceCount: currentCardSessionRepeatsRef.current,
      isFirstSessionRep: Boolean(cardId && !sessionScoredCardIdsRef.current.has(cardId)),
      isFrontSide: !isRevealed,
      level: PracticeSettingsService.getCardLevel(card),
      cardType: PracticeSettingsService.getCardType(card),
      completedReviewMode: isCompletedReviewMode,
    });
  };

  const getAutoRepeatProgressForCard = (card, repeatCount) => {
    const cardId = getCardId(card);
    const maxScoreGain = isCompletedReviewMode
      ? Infinity
      : Math.max(0, getEffectiveScoreLimit(card) - PracticeSettingsService.getCardScore(card));

    return calculateAutoRepeatProgress({
      practiceCount: currentCardSessionRepeatsRef.current,
      repeatCount,
      maxScoreGain,
      isFirstSessionRep: Boolean(cardId && !sessionScoredCardIdsRef.current.has(cardId)),
      isFrontSide: !isRevealed,
      level: PracticeSettingsService.getCardLevel(card),
      cardType: PracticeSettingsService.getCardType(card),
      completedReviewMode: isCompletedReviewMode,
    });
  };

  const recordScoreForPracticeIncrease = (card, scoreDelta, bonuses = []) => {
    if (isWritingReview) return;
    const cardId = getCardId(card);
    if (cardId) {
      sessionScoredCardIdsRef.current.add(cardId);
    }
    scoreBonusEventIdRef.current += 1;
    setCurrentScoreBonusEvent({
      id: scoreBonusEventIdRef.current,
      bonuses,
    });
    const safeScoreDelta = Number(scoreDelta) || 0;
    currentCardSessionScoreGainedRef.current += safeScoreDelta;
    totalScoreGainedRef.current += safeScoreDelta;
    setCurrentCardSessionScoreGained(currentCardSessionScoreGainedRef.current);
    setTotalScoreGained(totalScoreGainedRef.current);
  };

  const showEarlyGradeThreePopup = (card, previousGrade, nextGrade) => {
    if (!shouldShowEarlyGradeThreePopup(card, previousGrade, nextGrade)) return false;

    const trainerId = getCurrentTrainerId();
    enqueueCelebrationPopup({
      kind: 'earlyGradeThree',
      iconLayout: 'speech',
      title: t('gradeThreeMilestone.title'),
      message: t('gradeThreeMilestone.message', undefined, {
        score: PracticeSettingsService.getCardScore(card),
      }),
      iconSrc: getEarlyGradeThreeTrainerImage(trainerId),
      iconAlt: t('gradeThreeMilestone.iconAlt'),
    });

    return true;
  };

  const applyDisplayedGrade = (grade, cueMessage = null) => {
    const changed = selectedRatingRef.current !== grade;
    selectedRatingRef.current = grade;
    setSelectedRating(grade);

    if (changed || cueMessage) {
      showAutoGradeCue(cueMessage, grade);
    }
  };

  const persistGrade = async (
    grade,
    {
      manual = false,
      force = false,
      cueMessage = null,
      graduationType = null,
      finishPopupDelayMs = 0,
    } = {}
  ) => {
    const card = currentCardRef.current;
    const cardId = getCardId(card);
    if (!cardId) return { updatedCard: card, finished: false, skipped: true };

    const savedGrade = PracticeSettingsService.getAssessmentGrade(card);
    if (!force && !manual) {
      if (manualGradeOverrideRef.current || isAutoGradeLocked(card)) {
        return { updatedCard: card, finished: false, skipped: true };
      }
    }

    if (manual) {
      manualGradeOverrideRef.current = true;
      pendingCardEndGradeRef.current = null;
    }

    let resolvedGrade = grade;
    let resolvedGraduationType = graduationType;
    let resolvedTestIndex = PracticeSettingsService.calculateNextTestIndex(card, grade);
    let resolvedCueMessage = cueMessage;
    const persistCardType = PracticeSettingsService.getCardType(card);
    const isRepeatModePersistCard = persistCardType === PracticeSettingsService.CARD_TYPES.READING;

    if (
      !isRepeatModePersistCard &&
      grade === 4 &&
      PracticeSettingsService.shouldEarlyGraduateAfterTest(card, resolvedTestIndex)
    ) {
      resolvedGrade = 5;
      resolvedGraduationType = PracticeSettingsService.GRADUATION_TYPES.EARLY;
      resolvedTestIndex = 0;
      resolvedCueMessage = 'Early Graduation!';
    } else if (resolvedGrade === 5) {
      resolvedGraduationType = PracticeSettingsService.resolveGraduationType(
        card,
        resolvedGrade,
        resolvedGraduationType
      );
      resolvedTestIndex = 0;
    }

    if (savedGrade !== resolvedGrade) {
      setIsGradeUpdated(true);
    }
    applyDisplayedGrade(resolvedGrade, resolvedCueMessage);
    markCardStudied(cardId);

    if (savedGrade === resolvedGrade && resolvedGrade !== 4) {
      const finished = resolvedGrade === 5 || isGradeFinished(resolvedGrade);
      if (finished) {
        if (finishPopupDelayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, finishPopupDelayMs));
        }
        triggerBoardBreak(card);
      }
      return { updatedCard: card, finished, skipped: true };
    }

    const timestamp = new Date().toISOString();
    const updatedCard = buildLocallyEvaluatedCard(
      card,
      resolvedGrade,
      timestamp,
      resolvedGraduationType,
      resolvedTestIndex
    );
    syncUpdatedCard(updatedCard);

    updatePendingCardProgress(cardId, (current) => ({
      ...current,
      practiceCountFloor: resolvedGrade === 4
        ? Math.max(current.practiceCountFloor || 0, GRADE_FOUR_MIN_REPEAT_COUNT)
        : current.practiceCountFloor ?? null,
      testIndex: updatedCard.testIndex,
      graduationType: PracticeSettingsService.normalizeGraduationType(updatedCard.graduationType) ||
        current.graduationType ||
        null,
      assessments: [
        ...(current.assessments || []),
        {
          grade: resolvedGrade,
          assessedAt: timestamp,
          practiceCountAt: PracticeSettingsService.getCardPracticeCount(card),
          scoreAt: PracticeSettingsService.getCardScore(card),
          testIndexAfter: updatedCard.testIndex,
        },
      ],
    }));

    const sessionResult = { cardId, grade: resolvedGrade, timestamp };
    sessionResultsRef.current = [...sessionResultsRef.current, sessionResult];
    setSessionResults(sessionResultsRef.current);

    showEarlyGradeThreePopup(updatedCard, savedGrade, resolvedGrade);

    const finished = resolvedGrade === 5 || isGradeFinished(resolvedGrade);
    if (finished) {
      if (finishPopupDelayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, finishPopupDelayMs));
      }
      triggerBoardBreak(updatedCard);
    }

    return { updatedCard, finished, skipped: false };
  };

  const persistScoreGraduation = async (
    card,
    { cueMessage = 'Point goal complete!', finishPopupDelayMs = 0 } = {}
  ) => {
    const cardId = getCardId(card);
    if (!cardId) return { updatedCard: card, finished: false, skipped: true };
    if (PracticeSettingsService.getCardGraduationType(card) || card.completedAt) {
      return { updatedCard: card, finished: true, skipped: true };
    }

    const timestamp = new Date().toISOString();
    const updatedCard = {
      ...card,
      graduationType: PracticeSettingsService.GRADUATION_TYPES.SCORE,
      completedAt: card.completedAt || timestamp,
    };

    if (cueMessage) showAutoGradeCue(cueMessage);
    markCardStudied(cardId);
    syncUpdatedCard(updatedCard);
    updatePendingCardProgress(cardId, (current) => ({
      ...current,
      graduationType: PracticeSettingsService.GRADUATION_TYPES.SCORE,
      completedAt: current.completedAt || updatedCard.completedAt,
    }));

    if (finishPopupDelayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, finishPopupDelayMs));
    }
    triggerBoardBreak(updatedCard);
    return { updatedCard, finished: true, skipped: false };
  };

  const handleAutoRepeatCard = async () => {
    if (isTtsInteractionLockedRef.current) return;
    if (
      cardAdvancePendingRef.current ||
      advanceInFlightRef.current ||
      isProcessing ||
      boardBreakPopupOpenRef.current ||
      achievementActiveRef.current ||
      npcEncounterRef.current
    ) return;
    if (isViewingHistoryCard()) return;

    const card = currentCardRef.current;
    const cardId = getCardId(card);
    if (!cardId) return;

    const remainingRepeats = Math.max(0, currentCardTargetRepeatsRef.current - currentCardSessionRepeatsRef.current);
    if (remainingRepeats <= 0) return;

    const practicedAt = new Date().toISOString();
    const autoRepeatProgress = getAutoRepeatProgressForCard(card, remainingRepeats);
    const repeatsGained = autoRepeatProgress.repeatCount;
    const scoreDelta = autoRepeatProgress.scoreDelta;
    const bonuses = autoRepeatProgress.bonuses;
    if (repeatsGained <= 0 || scoreDelta <= 0) return;

    const newRepeats = currentCardSessionRepeatsRef.current + repeatsGained;
    const locallyUpdatedCard = {
      ...card,
      practiceCount: PracticeSettingsService.getCardPracticeCount(card) + repeatsGained,
      score: PracticeSettingsService.getCardScore(card) + scoreDelta,
      lastPracticedAt: practicedAt,
    };

    clearMessageButton();
    releaseAnswerChoiceModeAfterPracticeCount();
    markCurrentCardPracticed();
    playPracticeSound('autoRepeat', { recoverOnGesture: true });
    recordScoreForPracticeIncrease(card, scoreDelta, bonuses);
    currentCardDisplayPracticeIncrementsRef.current += repeatsGained;
    recordQuickTestRepeatAttempt();
    currentCardSessionRepeatsRef.current = newRepeats;
    setCurrentCardSessionRepeats(newRepeats);
    setTotalRepeatCount((prev) => prev + repeatsGained);
    syncUpdatedCard(locallyUpdatedCard);
    updatePendingCardProgress(cardId, (current) => ({
      ...current,
      practiceCountDelta: (current.practiceCountDelta || 0) + repeatsGained,
      scoreDelta: (current.scoreDelta || 0) + scoreDelta,
      lastPracticedAt: practicedAt,
    }));

    const projectedScore = getProjectedScore(card, scoreDelta);
    if (!isCompletedReviewMode && projectedScore >= getEffectiveScoreLimit(card)) {
      cardAdvancePendingRef.current = true;
      await persistScoreGraduation(currentCardRef.current || locallyUpdatedCard, {
        cueMessage: 'Point goal complete!',
        finishPopupDelayMs: AUTO_REPEAT_GRADUATION_DELAY_MS,
      });
      if (!boardBreakPopupOpenRef.current && !activeCelebrationPopupRef.current) {
        scheduleAdvanceAfterTarget();
      }
      return;
    }

    scheduleAdvanceAfterTarget();
  };

  const handleAutoRepeatConfirm = async () => {
    void unlockPracticeAudio();
    if (isTtsInteractionLockedRef.current) return;
    if (isProcessing || boardBreakPopupOpenRef.current || achievementActiveRef.current || npcEncounterRef.current) return;
    if (isViewingHistoryCard()) return;

    const card = currentCardRef.current;
    if (!card) return;

    const remainingRepeats = Math.max(0, currentCardTargetRepeatsRef.current - currentCardSessionRepeatsRef.current);
    if (remainingRepeats <= 0) return;

    const preview = getAutoRepeatProgressForCard(card, remainingRepeats);
    if (preview.repeatCount <= 0 || preview.scoreDelta <= 0) return;

    const reachesScoreLimit = !isCompletedReviewMode &&
      PracticeSettingsService.getCardScore(card) + preview.scoreDelta >= getEffectiveScoreLimit(card);
    const repeatWord = preview.repeatCount === 1
      ? t('autoRepeat.repeatSingular')
      : t('autoRepeat.repeatPlural');
    const message = reachesScoreLimit
      ? t('autoRepeat.limitMessage', undefined, {
        count: preview.repeatCount,
        repeatWord,
        score: preview.scoreDelta,
      })
      : t('autoRepeat.fillMessage', undefined, {
        count: preview.repeatCount,
        repeatWord,
        score: preview.scoreDelta,
      });

    const confirmed = await confirm({
      title: t('autoRepeat.title'),
      message,
      confirmLabel: t('autoRepeat.confirm'),
      cancelLabel: t('common.cancel'),
      tone: 'warning',
    });

    if (!confirmed) return;

    void unlockPracticeAudio();
    await handleAutoRepeatCard();
  };

  const recordSingleFlashcardRepeat = async ({ fromListening = false } = {}) => {
    if (importPaused) return;
    // Hearing the first answer is not a repeat until the user chooses X or O.
    if (!isCompletedReviewMode && isRevealed && !answerDecisionMadeRef.current) return;
    if (cardAdvancePendingRef.current || advanceInFlightRef.current) {
      if (cardAdvancePendingRef.current) scheduleAdvanceAfterTarget();
      return;
    }
    if (isProcessing || boardBreakPopupOpenRef.current || achievementActiveRef.current || npcEncounterRef.current) return;
    if (isViewingHistoryCard()) return;
    if (isTtsInteractionLockedRef.current && !fromListening) return;
    if (flashcardRepeatInputLockedRef.current && !fromListening) return;
    const card = currentCardRef.current;
    const cardId = getCardId(card);
    if (!cardId) return;
    if (hasReachedPracticeTarget(
      currentCardSessionRepeatsRef.current,
      currentCardTargetRepeatsRef.current
    )) {
      scheduleAdvanceAfterTarget();
      return;
    }
    startFlashcardRepeatInputLock();
    const newRepeats = getNextPracticeRepeatCount(
      currentCardSessionRepeatsRef.current,
      currentCardTargetRepeatsRef.current
    );
    const practicedAt = new Date().toISOString();
    const rawScoreBreakdown = getPracticeScoreBreakdown(card);
    const scoreDelta = getLimitedScoreDelta(card, rawScoreBreakdown.scoreDelta);
    const bonuses = scoreDelta === rawScoreBreakdown.scoreDelta
      ? rawScoreBreakdown.bonuses
      : rawScoreBreakdown.bonuses.filter((bonus) => bonus.points <= scoreDelta);
    const locallyUpdatedCard = buildLocallyPracticedCard(card, practicedAt, scoreDelta, !fromListening);

    clearMessageButton();
    releaseAnswerChoiceModeAfterPracticeCount();
    markCurrentCardPracticed();
    playPracticeSound('repeat', {
      repeatIndex: newRepeats,
      targetRepeats: currentCardTargetRepeatsRef.current,
      recoverOnGesture: !fromListening,
    });
    recordScoreForPracticeIncrease(card, scoreDelta, bonuses);
    currentCardDisplayPracticeIncrementsRef.current += 1;
    recordQuickTestRepeatAttempt();
    currentCardSessionRepeatsRef.current = newRepeats;
    setCurrentCardSessionRepeats(newRepeats);
    setTotalRepeatCount((prev) => prev + 1);
    syncUpdatedCard(locallyUpdatedCard);
    updatePendingCardProgress(cardId, (current) => ({
      ...current,
      practiceCountDelta: (current.practiceCountDelta || 0) + 1,
      scoreDelta: (current.scoreDelta || 0) + scoreDelta,
      lastPracticedAt: fromListening ? current.lastPracticedAt : practicedAt,
    }));

    const projectedScore = getProjectedScore(card, scoreDelta);

    if (!isCompletedReviewMode && projectedScore >= getEffectiveScoreLimit(card)) {
      cardAdvancePendingRef.current = true;
      await persistScoreGraduation(currentCardRef.current || locallyUpdatedCard, {
        cueMessage: 'Point goal complete!',
      });
      if (!boardBreakPopupOpenRef.current && !activeCelebrationPopupRef.current) {
        scheduleAdvanceAfterTarget();
      }
      return;
    }

    if (newRepeats >= currentCardTargetRepeatsRef.current) {
      scheduleAdvanceAfterTarget();
    }
  };

  const handleFlip = async () => recordSingleFlashcardRepeat();

  const handleTtsPlaybackCompleted = async ({ trigger } = {}) => {
    isTtsInteractionLockedRef.current = false;
    setIsTtsInteractionLocked(false);
    if (trigger === 'auto') return;
    await recordSingleFlashcardRepeat({ fromListening: true });
  };

  const handleReveal = ({ direction, from, to } = {}) => {
    void unlockPracticeAudio();
    if (isTtsInteractionLockedRef.current || cardAdvancePendingRef.current || advanceInFlightRef.current) return;
    if (!answerFeedbackFlipRef.current) playCardFlip();
    answerFeedbackFlipRef.current = false;
    disableQuickTestTimerForCurrentCard();
    setIsTtsAnswerVisible(false);
    setIsRevealed((prev) => !prev);

    const isAnswerToQuestion = reverseMode
      ? from === 'front' && to === 'back'
      : from === 'back' && to === 'front';
    const isMessageTriggerFlip = direction === 'left' && isAnswerToQuestion;
    if (isMessageTriggerFlip && !messageButtonRevealHandledRef.current) {
      const card = currentCardRef.current;
      messageButtonRevealHandledRef.current = true;
      if (
        !isCompletedReviewMode &&
        !isViewingHistoryCard() &&
        shouldOfferCardMessageOnReveal({ card, direction, from, to, reverseMode })
      ) {
        setMessageButtonCardId(getCardId(card));
      }
    }

    if (isViewingHistoryCard()) {
      return;
    }

    // Set pending grade immediately when the reveal animation starts so a quick advance
    // still counts as a reveal instead of a no-flip completion.
    if (to === 'back') {
      void applyAnswerRevealGrade();
    } else if (to === 'front') {
      answerRevealGradeAppliedRef.current = false;
    }
  };

  const handleAnswerViewed = () => {
    void unlockPracticeAudio();
    if (isTtsInteractionLockedRef.current || cardAdvancePendingRef.current || advanceInFlightRef.current) return;
    if (isViewingHistoryCard()) return;
    if (isCompletedReviewMode) return;
    void applyAnswerRevealGrade();
  };

  const handleAnswerOk = () => {
    void unlockPracticeAudio();
    if (isTtsInteractionLockedRef.current || cardAdvancePendingRef.current || advanceInFlightRef.current) return;
    if (isViewingHistoryCard()) return;
    if (isCompletedReviewMode) return;
    playAnswerFeedback('ok');
    answerFeedbackFlipRef.current = true;
    clearMessageButton();
    answerViewedRef.current = true;
    answerOkRef.current = true;
    answerDecisionMadeRef.current = true;
    setHasAnswerDecision(true);
    pendingCardEndGradeRef.current = null;

    persistGrade(2, {
      cueMessage: 'Auto: grade 2',
    });
  };

  const handleAnswerNo = () => {
    void unlockPracticeAudio();
    if (isTtsInteractionLockedRef.current || cardAdvancePendingRef.current || advanceInFlightRef.current) return;
    if (isViewingHistoryCard() || isCompletedReviewMode) return;
    playAnswerFeedback('no');
    answerFeedbackFlipRef.current = true;
    clearMessageButton();
    answerDecisionMadeRef.current = true;
    setHasAnswerDecision(true);
    answerViewedRef.current = true;
    answerOkRef.current = false;
  };

  const handleRevealComplete = ({ to } = {}) => {
    const nextAnswerVisible = reverseMode ? to === 'front' : to === 'back';
    setIsTtsAnswerVisible(nextAnswerVisible);
  };

  const handleReadingSentenceRepeat = async ({
    childCardId,
    sentenceText,
    sentenceRepeatCount,
    targetRepeats,
    itemType,
    speechSide,
    completedWithoutFlip,
  } = {}) => {
    if (isProcessing || boardBreakPopupOpenRef.current || achievementActiveRef.current) return;
    if (isViewingHistoryCard()) return;
    const card = currentCardRef.current;
    const childCard = card?.readingChildren?.find((child) => getCardId(child) === childCardId) || null;
    const progressCard = childCard || card;
    const cardId = getCardId(progressCard);
    if (!cardId) return;

    const practicedAt = new Date().toISOString();
    const scoreBreakdown = calculateReadingPracticeScoreBreakdown({
      sentenceText,
      repeatCount: 1,
      itemType,
      speechSide,
      completedWithoutFlip,
    });
    const { sessionScoreDelta, cardScoreDelta } = allocateReadingPracticeScore(
      scoreBreakdown.scoreDelta,
      getLimitedScoreDelta(progressCard, scoreBreakdown.scoreDelta)
    );
    const bonuses = scoreBreakdown.bonuses;
    const locallyUpdatedCard = {
      ...progressCard,
      score: PracticeSettingsService.getCardScore(progressCard) + cardScoreDelta,
      lastPracticedAt: practicedAt,
    };

    markCurrentCardPracticed();
    playPracticeSound('repeat', {
      repeatIndex: sentenceRepeatCount,
      targetRepeats,
      recoverOnGesture: true,
    });
    recordScoreForPracticeIncrease(progressCard, sessionScoreDelta, bonuses);
    setTotalRepeatCount((prev) => prev + 1);
    syncUpdatedCard(locallyUpdatedCard);
    updatePendingCardProgress(cardId, (current) => ({
      ...current,
      scoreDelta: (current.scoreDelta || 0) + cardScoreDelta,
      lastPracticedAt: practicedAt,
    }));
  };

  const handleReadingSentenceAutoRepeat = async ({
    childCardId,
    fromRepeatCount,
    targetRepeats,
    sentenceText,
    itemType,
    speechSide,
    completedWithoutFlip,
  }) => {
    if (isProcessing || boardBreakPopupOpenRef.current || achievementActiveRef.current) return;
    if (isViewingHistoryCard()) return;

    const card = currentCardRef.current;
    const childCard = card?.readingChildren?.find((child) => getCardId(child) === childCardId) || null;
    const progressCard = childCard || card;
    const cardId = getCardId(progressCard);
    if (!cardId) return;

    const safeTargetRepeats = Math.max(1, Number(targetRepeats) || currentCardTargetRepeatsRef.current || 1);
    const safeFromRepeatCount = Math.max(0, Number(fromRepeatCount) || 0);
    const repeatsGained = Math.max(0, safeTargetRepeats - safeFromRepeatCount);
    if (repeatsGained <= 0) return;

    const practicedAt = new Date().toISOString();
    const scoreBreakdown = calculateReadingPracticeScoreBreakdown({
      sentenceText,
      repeatCount: repeatsGained,
      itemType,
      speechSide,
      completedWithoutFlip,
    });
    const { sessionScoreDelta, cardScoreDelta } = allocateReadingPracticeScore(
      scoreBreakdown.scoreDelta,
      getLimitedScoreDelta(progressCard, scoreBreakdown.scoreDelta)
    );
    const bonuses = [
      { label: 'Auto Sentence', points: sessionScoreDelta },
      ...scoreBreakdown.bonuses,
    ];
    const locallyUpdatedCard = {
      ...progressCard,
      score: PracticeSettingsService.getCardScore(progressCard) + cardScoreDelta,
      lastPracticedAt: practicedAt,
    };

    markCurrentCardPracticed();
    playPracticeSound('autoRepeat', { recoverOnGesture: true });
    recordScoreForPracticeIncrease(progressCard, sessionScoreDelta, bonuses);
    setTotalRepeatCount((prev) => prev + repeatsGained);
    syncUpdatedCard(locallyUpdatedCard);
    updatePendingCardProgress(cardId, (current) => ({
      ...current,
      scoreDelta: (current.scoreDelta || 0) + cardScoreDelta,
      lastPracticedAt: practicedAt,
    }));
  };

  const handleRepeatModeCardComplete = async ({ targetRepeats }) => {
    void unlockPracticeAudio();
    if (isProcessing || boardBreakPopupOpenRef.current || achievementActiveRef.current) return;
    if (isViewingHistoryCard()) return;
    const card = currentCardRef.current;
    const cardId = getCardId(card);
    if (!cardId) return;

    const visitKey = `${cardId}:${currentCardDisplayTokenRef.current}:${readingResetKey}`;
    if (completedReadingVisitRef.current === visitKey) return;
    completedReadingVisitRef.current = visitKey;
    const wasCompleted = PracticeSettingsService.isCardCompleted(card);
    // Every sentence receives the page's grade-based increment, even once-only
    // or already completed sentences. Do not average differing drill targets.
    // Partial pages retain points only; duplicate completion is guarded above.
    const practicedAt = new Date().toISOString();
    const repeatsGained = Math.max(1, Number(targetRepeats) || currentCardTargetRepeatsRef.current || 1);
    const progressCards = card.readingChildren?.length
      ? card.readingChildren.filter((child) => !child.retiredAt)
      : [card];
    progressCards.forEach((progressCard) => {
      const practiceCount = PracticeSettingsService.getCardPracticeCount(progressCard) + repeatsGained;
      const completion = practiceCount >= PracticeSettingsService.getCardScoreLimit(progressCard)
        ? { completedAt: progressCard.completedAt || practicedAt, graduationType: progressCard.graduationType || 'score' }
        : {};
      syncUpdatedCard({ ...progressCard, practiceCount, lastPracticedAt: practicedAt, ...completion });
      updatePendingCardProgress(getCardId(progressCard), (current) => ({
        ...current,
        practiceCountDelta: (current.practiceCountDelta || 0) + repeatsGained,
        lastPracticedAt: practicedAt,
        ...completion,
      }));
    });

    currentCardSessionRepeatsRef.current = repeatsGained;
    setCurrentCardSessionRepeats(repeatsGained);
    const updatedReadingCard = currentCardRef.current || card;
    if (!isCompletedReviewMode && !wasCompleted && PracticeSettingsService.isCardCompleted(updatedReadingCard)) {
      triggerBoardBreak({
        ...updatedReadingCard,
        graduationType: PracticeSettingsService.getCardGraduationType(updatedReadingCard) || PracticeSettingsService.GRADUATION_TYPES.SCORE,
      });
      return;
    }
    scheduleAdvanceAfterTarget(250);
  };

  const handleEditSubmit = async (formData) => {
    if (isQuickMode) return;
    const cardId = getCardId(editingCardRef.current || currentCardRef.current);
    try {
      const updatedCard = await apiClient.updateFlashCard(cardId, formData);
      syncUpdatedCard(updatedCard);
      setIsEditModalOpen(false);
      setEditingCard(null);
      editingCardRef.current = null;
    } catch (err) {
      console.error('Failed to update card:', err);
      setError(err);
      throw err;
    }
  };

  const handleEditLevelUp = async (formData) => {
    if (isQuickMode) return;
    const card = editingCardRef.current || currentCardRef.current;
    const cardId = getCardId(card);
    if (!cardId) return;

    try {
      await flushPendingPracticeProgress();
      const result = await apiClient.levelUpFlashCard(cardId, formData);
      syncUpdatedCard(result.sourceCard);
      setIsEditModalOpen(false);
      setIsLevelUpModalOpen(false);
      setEditingCard(null);
      editingCardRef.current = null;
      const level = PracticeSettingsService.getCardLevel(result.createdCard);
      enqueueCelebrationPopup({
        kind: 'levelUp',
        title: t('levelUp.title'),
        message: t('levelUp.successMessage', undefined, { level }),
        ...getCardLevelUpAnimation(level),
      });
    } catch (err) {
      console.error('Failed to level up card from edit form:', err);
      setError(err);
      throw err;
    }
  };

  const handleDeleteCurrentCard = async () => {
    if (isQuickMode) return;
    const cardToDelete = editingCardRef.current || currentCard;
    if (!cardToDelete || isProcessing || achievementActiveRef.current) return;

    const confirmed = await confirm({
      title: t('practiceUi.delete.title'),
      message: t('practiceUi.delete.message'),
      confirmLabel: t('practiceUi.delete.confirm'),
      cancelLabel: t('practiceUi.delete.cancel'),
      tone: 'danger',
    });
    if (!confirmed) return;

    const cardId = getCardId(cardToDelete);
    const remainingCards = cardPoolRef.current.filter((card) => getCardId(card) !== cardId);
    const filteredHistory = cardHistoryRef.current.filter((card) => getCardId(card) !== cardId);
    const filteredRecentIds = recentCardIdsRef.current.filter((id) => id !== cardId);
    const filteredSessionQueue = sessionQueueRef.current.filter((card) => getCardId(card) !== cardId);

    setIsProcessing(true);
    setError(null);

    try {
      await apiClient.deleteFlashCard(cardId);
      seenCycleIdsRef.current.delete(cardId);
      pendingCardProgressRef.current.delete(cardId);
      syncPendingProgressCount();

      cardPoolRef.current = remainingCards;
      sessionQueueRef.current = filteredSessionQueue;
      setCardPool(remainingCards);
      setSessionQueue(filteredSessionQueue);
      setCardHistory(filteredHistory);
      setRecentCardIds(filteredRecentIds);
      setIsEditModalOpen(false);
      setEditingCard(null);
      editingCardRef.current = null;

      if (onFlashCardsUpdate) {
        onFlashCardsUpdate(remainingCards);
      }

      const nextQueueIndex = Math.min(sessionQueueIndexRef.current, filteredSessionQueue.length - 1);
      const nextCard = nextQueueIndex >= 0 ? resolveQueuedCard(filteredSessionQueue[nextQueueIndex]) : null;

      if (!nextCard) {
        await performEndSession();
        return;
      }

      sessionQueueIndexRef.current = nextQueueIndex;
      setSessionQueueIndex(nextQueueIndex);
      navigationStackRef.current = [nextCard];
      navigationIndexRef.current = 0;
      setNavigationStack([nextCard]);
      setNavigationIndex(0);
      setCardHistory([]);

      setSlideDirection('left');
      setTimeout(() => {
        setReadingResetKey((key) => key + 1);
        setCurrentCard(nextCard);
        setDisplayedNavigationIndex(0);
        setCurrentCardTargetRepeats(getTargetRepeatsForCard(nextCard));
        setCurrentCardSessionRepeats(0);
        setIsRevealed(false);
        setSlideDirection('');
      }, 220);
    } catch (err) {
      console.error('Failed to delete card:', err);
      setError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEvaluation = async (grade) => {
    if (isTtsInteractionLockedRef.current || cardAdvancePendingRef.current || advanceInFlightRef.current) return;
    void unlockPracticeAudio();
    if (isProcessing || boardBreakPopupOpenRef.current || achievementActiveRef.current || npcEncounterRef.current) return;
    if (isViewingHistoryCard()) return;
    if (isCompletedReviewMode) return;

    clearMessageButton();
    markCurrentCardPracticed();
    answerViewedRef.current = answerViewedRef.current || isRevealed;
    answerOkRef.current = grade === 3;
    pendingCardEndGradeRef.current = null;

    const result = await persistGrade(grade, {
      manual: true,
      graduationType: grade === 5 ? PracticeSettingsService.GRADUATION_TYPES.MANUAL : null,
    });
    if (result?.finished) {
      cardAdvancePendingRef.current = true;
      if (!boardBreakPopupOpenRef.current && !activeCelebrationPopupRef.current) {
        scheduleAdvanceAfterTarget();
      }
      return;
    }

    if (currentCardSessionRepeatsRef.current >= currentCardTargetRepeatsRef.current) {
      scheduleAdvanceAfterTarget(300);
    }
  };

  const continueAfterNpcEncounter = () => {
    const pendingAdvance = pendingAdvanceAfterEncounterRef.current;
    pendingAdvanceAfterEncounterRef.current = null;
    npcEncounterRef.current = null;
    setNpcEncounter(null);

    if (pendingAdvance) {
      advanceInFlightRef.current = true;
      pendingAdvance();
    }
  };

  const handleNpcEncounterBeforeSceneDone = () => {
    setNpcEncounter((current) => current ? { ...current, phase: 'prompt' } : current);
  };

  const handleNpcEncounterFight = () => {
    setNpcEncounter((current) => current ? { ...current, phase: 'battle' } : current);
  };

  const handleNpcEncounterSkip = () => {
    continueAfterNpcEncounter();
  };

  const handleNpcEncounterProgress = ({ card: progressCard } = {}, { points = 1, repeated = true } = {}) => {
    const cardId = getCardId(progressCard);
    if (!cardId) return { cardScoreDelta: 0, sessionScoreDelta: 0 };

    const encounterCard = (npcEncounterRef.current?.cards || [])
      .find((card) => getCardId(card) === cardId);
    const storedCard = cardPoolRef.current.find((card) => getCardId(card) === cardId);
    const card = encounterCard || storedCard || progressCard;
    const practicedAt = new Date().toISOString();
    const cardScoreDelta = getLimitedScoreDelta(card, points);
    const updatedCard = {
      ...card,
      practiceCount: PracticeSettingsService.getCardPracticeCount(card) + Number(repeated),
      score: PracticeSettingsService.getCardScore(card) + cardScoreDelta,
      lastPracticedAt: practicedAt,
    };

    markCardStudied(cardId);
    const nextRepeatTotal = totalRepeatCountRef.current + Number(repeated);
    totalRepeatCountRef.current = nextRepeatTotal;
    setTotalRepeatCount(nextRepeatTotal);
    const nextScoreTotal = totalScoreGainedRef.current + points;
    totalScoreGainedRef.current = nextScoreTotal;
    setTotalScoreGained(nextScoreTotal);

    updatePendingCardProgress(cardId, (current) => ({
      ...current,
      practiceCountDelta: (current.practiceCountDelta || 0) + Number(repeated),
      scoreDelta: (current.scoreDelta || 0) + cardScoreDelta,
      lastPracticedAt: practicedAt,
    }));
    syncUpdatedCard(updatedCard);

    return { cardScoreDelta, sessionScoreDelta: points };
  };

  const handleNpcEncounterPracticeRepeat = result => handleNpcEncounterProgress(result);
  const handleNpcEncounterAnswerViewed = result => handleNpcEncounterProgress(result, { points: 2, repeated: false });

  const handleNpcEncounterCardResult = ({ card } = {}) => {
    const encounter = npcEncounterRef.current;
    const cardId = getCardId(card);
    if (encounter?.phase !== 'battle' || !cardId || npcJudgedCardIdsRef.current.has(cardId) ||
        !encounter.cards.some(item => getCardId(item) === cardId)) return { sessionScoreDelta: 0 };
    npcJudgedCardIdsRef.current.add(cardId);
    markCardStudied(cardId);
    // A judgment rewards this session, not the completed card's mastery score.
    const nextTotal = totalScoreGainedRef.current + ENCOUNTER_JUDGMENT_POINTS;
    totalScoreGainedRef.current = nextTotal;
    setTotalScoreGained(nextTotal);
    return { sessionScoreDelta: ENCOUNTER_JUDGMENT_POINTS };
  };

  const handleNpcEncounterComplete = async (result) => {
    const passedIds = new Set(result?.passedCardIds || []);
    const practicedAt = new Date().toISOString();

    if (passedIds.size > 0) {
      (npcEncounterRef.current?.cards || []).forEach((card) => {
        const cardId = getCardId(card);
        if (!cardId || !passedIds.has(cardId)) return;

        updatePendingCardProgress(cardId, (current) => ({
          ...current,
          lastPracticedAt: practicedAt,
        }));

        syncUpdatedCard({
          ...card,
          lastPracticedAt: practicedAt,
        });
      });
    }

    const scoreDelta = Number(result?.scoreDelta) || 0;
    if (scoreDelta !== 0) {
      const nextTotalScore = totalScoreGainedRef.current + scoreDelta;
      totalScoreGainedRef.current = nextTotalScore;
      setTotalScoreGained(nextTotalScore);
    }

    const resultSceneId = getNpcEncounterResultSceneId(
      npcEncounterRef.current?.beforeScene?.id,
      result?.sceneOutcome
    );
    let resultScene = null;
    if (resultSceneId) {
      try {
        const sceneResult = await apiClient.getSceneById(resultSceneId, {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        resultScene = sceneResult?.scene || null;
      } catch (err) {
        console.warn('Failed to load NPC encounter result scene:', err);
      }
    }

    setNpcEncounter((current) => current ? {
      ...current,
      phase: resultScene ? 'resultScene' : 'resultSummary',
      resultScene,
      result,
    } : current);
  };

  const completeSession = (payload) => {
    const shouldShowTimeCompletion =
      payload?.elapsedTime >= SESSION_TIME_LIMIT_SECONDS &&
      !timeCompletionCelebrationShownRef.current;
    const shouldShowQueueExhausted =
      payload?.endReason === 'queueExhausted' &&
      !queueExhaustedCelebrationShownRef.current;

    if (shouldShowTimeCompletion) {
      timeCompletionCelebrationShownRef.current = true;
      enqueueCelebrationPopup({
        kind: 'timeCompletion',
        title: t('practiceUi.completion.timeTitle'),
        message: t('practiceUi.completion.timeMessage'),
        animationSpriteSrc: timeCompletionCelebrationSprite,
        animationFrameCount: 8,
        animationFrameSize: 128,
        animationDurationMs: 960,
      });
    }

    if (shouldShowQueueExhausted) {
      queueExhaustedCelebrationShownRef.current = true;
      enqueueCelebrationPopup({
        kind: 'queueExhausted',
        title: t('practiceUi.completion.setTitle'),
        message: t('practiceUi.completion.setMessage'),
        animationSpriteSrc: nextRepeatNoSprite,
        animationFrameCount: 16,
        animationFrameSize: 128,
        animationDurationMs: 1000,
      });
    }

    // Drain every celebration first, including milestones created during final grading.
    pendingSessionCompletionRef.current = () => onSessionComplete?.(payload);
    showNextCelebrationPopupRef.current?.();
  };

  const performEndSession = async (options = {}) => {
    if (achievementActiveRef.current) return;
    cardAdvanceRequestIdRef.current += 1;
    pendingCardAdvanceContinuationRef.current = null;
    pendingAdvanceAfterEncounterRef.current = null;
    advanceInFlightRef.current = false;

    const resolvedEndReason = Object.prototype.hasOwnProperty.call(options, 'endReason')
      ? options.endReason
      : pendingSessionEndReasonRef.current;
    pendingSessionEndReasonRef.current = resolvedEndReason;

    if (sessionEndTimerRef.current) {
      clearTimeout(sessionEndTimerRef.current);
      sessionEndTimerRef.current = null;
    }

    setIsProcessing(true);
    setError(null);

    try {
      if (checkpointSavePromiseRef.current) {
        await checkpointSavePromiseRef.current;
      }
      await finalizeCardGradeForAdvance();

      const finalElapsedTime = elapsedTimeRef.current;
      const finalRepeatCount = totalRepeatCountRef.current;
      const finalScoreGained = totalScoreGainedRef.current;
      const finalStudiedCardIds = new Set(uniqueCardsStudiedRef.current);
      const finalCardsStudied = finalStudiedCardIds.size;
      const finalPracticedCards = cardPoolRef.current.filter((c) => finalStudiedCardIds.has(getCardId(c)));

      await flushPendingPracticeProgress();

      const response = await savePracticeSessionSnapshot({ isComplete: true });
      completeSession({
        cardsCompleted: finalCardsStudied,
        evaluations: sessionResultsRef.current,
        elapsedTime: finalElapsedTime,
        cardsStudied: finalCardsStudied,
        repeatCount: finalRepeatCount,
        scoreGained: finalScoreGained,
        practicedCards: finalPracticedCards,
        dailyStats: response?.dailyStats,
        sessionSaved: true,
        endReason: resolvedEndReason,
      });
    } catch (err) {
      console.error('Failed to save buffered practice progress:', err);
      setError({ message: t('interface.assessment.sessionSaveFailed'), sessionEndFailed: true });
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
      }
    }
  };

  performEndSessionRef.current = performEndSession;

  const handleEndSession = async () => {
    void unlockPracticeAudio();
    if (isTtsInteractionLockedRef.current) return;
    if (achievementActiveRef.current || isProcessing || hasActiveCelebrationPopup || npcEncounterRef.current) return;

    const confirmed = await confirm({
      title: t('practiceUi.end.title'),
      message: isQuickMode ? t('quickPractice.description') : t('practiceUi.end.message'),
      confirmLabel: t('practiceUi.end.confirm'),
      cancelLabel: t('practiceUi.end.cancel'),
      tone: 'warning',
    });

    if (!confirmed) return;

    await performEndSession({ endReason: null });
  };

  useEffect(() => {
    if (isConfirmDialogOpen) return undefined;

    const handlePracticeEscape = (event) => {
      if (event.key !== 'Escape' || event.repeat) return;
      const target = event.target;
      if (target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      if (
        importPaused || isEditModalOpen ||
        reviewCard || cardMenuOpen ||
        isLevelUpModalOpen ||
        isProcessing ||
        hasActiveCelebrationPopup ||
        hasActiveNpcEncounter ||
        achievementOverlay
      ) return;

      event.preventDefault();
      void handleEndSession();
    };

    window.addEventListener('keydown', handlePracticeEscape);
    return () => window.removeEventListener('keydown', handlePracticeEscape);
  });

  const handleTogglePracticeSound = async (event) => {
    event.stopPropagation();
    if (isTtsInteractionLockedRef.current) return;

    const nextEnabled = !soundEnabled;
    setPracticeSoundEnabled(nextEnabled);
    setTtsAutoplayEnabled(nextEnabled);
    setSoundEnabled(nextEnabled);
    if (nextEnabled) {
      await unlockPracticeAudio({
        forceReset: true,
        playTestPulse: true,
        forceEnabled: true,
      });
    }
  };

  const cardScore = displayedCardStats.score;
  const cardLevel = PracticeSettingsService.getCardLevel(currentCard);
  const cardScoreLimit = getEffectiveScoreLimit(currentCard);
  const daysSince = displayedCardStats.daysSince;
  const currentNoteName = getCurrentNoteName(currentCard, noteNameById);
  const currentNoteCover = getCurrentNoteCover(currentCard, noteCoverById);
  const currentCardTypeLabel = PracticeSettingsService.getCardTypeLabel(currentCard);
  const currentCardType = PracticeSettingsService.getCardType(currentCard);
  const isReadingCard = currentCardType === PracticeSettingsService.CARD_TYPES.READING;
  const progress = isReadingCard
    ? readingProgressPercent
    : currentCardTargetRepeats > 0
      ? Math.min(100, (currentCardSessionRepeats / currentCardTargetRepeats) * 100)
      : 0;
  const currentPracticePhase = PracticeSettingsService.getPracticePhase(currentCard);
  const isFluencyTestPhase = currentPracticePhase === PracticeSettingsService.PRACTICE_PHASES.MEMORY_TRIAL;
  const isFluencyTestFirstTry = isFluencyTestPhase && currentCardSessionRepeats === 0;
  const coachingTestIndex = PracticeSettingsService.getCardTestIndex(currentCard);
  const practiceCoachingTrigger = getPracticeCardCoachingTrigger({
    isFluencyTest: isFluencyTestPhase,
    isFirst: PracticeSettingsService.getCardPracticeCount(currentCard) === 0 && PracticeSettingsService.getCardScore(currentCard) === 0,
    grade: PracticeSettingsService.getAssessmentGrade(currentCard),
    testIndex: coachingTestIndex,
    isBack: isRevealed,
    answerDecisionMade: hasAnswerDecision,
    repeatCount: currentCardSessionRepeats,
  });
  // Older releases have one Fluency repeat pool; preserve it until the new
  // test-count pools are published, before falling back to the card sample.
  const displayedCoachingTrigger = [practiceCoachingTrigger,
    ...(practiceCoachingTrigger.startsWith('practice.card.phase3.fluency.test') ? ['practice.card.phase3.fluency'] : []),
    'practice.card.show'].find(trigger => coachingScenes.some(scene => scene?.trigger === trigger));
  const practicePhaseTitle = isFluencyTestPhase
    ? t('practicePhases.memoryTrial.title')
    : t('practicePhases.coreTraining.title');
  const currentPracticeCard = PracticeSettingsService.getPracticeDisplayCard(currentCard);
  const isRepeatModeCard = isReadingCard;
  const currentGradeLabel = isReadingCard
    ? PracticeSettingsService.getExposureLevel(currentCard)
    : PracticeSettingsService.getAssessmentGrade(currentCard);
  const currentCardId = getCardId(currentCard);
  const isHistoryReviewMode = navigationIndex < navigationStack.length - 1;
  const isReadingSentenceAdvancing = isReadingCard &&
    activeReadingSentence?.cardId === String(currentCardId) &&
    activeReadingSentence?.resetKey === readingResetKey &&
    activeReadingSentence?.isAdvancing;
  const hasActiveCelebrationPopup = Boolean(activeCelebrationPopup);
  const hasActiveNpcEncounter = Boolean(npcEncounter);
  const soundButtonLabel = t(soundEnabled ? 'interface.practiceAudio.offAction' : 'interface.practiceAudio.onAction');
  const soundButtonTitle = t(soundEnabled ? 'interface.practiceAudio.on' : 'interface.practiceAudio.off');
  const canAutoRepeatCurrentCard =
    !importPaused && !isHistoryReviewMode &&
    !cardAdvancePendingRef.current &&
    !advanceInFlightRef.current &&
    !isTtsInteractionLocked &&
    !isProcessing &&
    !hasActiveCelebrationPopup &&
    !hasActiveNpcEncounter &&
    !achievementOverlay &&
    !boardBreakPopupOpenRef.current &&
    currentCardSessionRepeats < currentCardTargetRepeats;
  const isCurrentCardAdvanceLocked =
    importPaused || cardAdvancePendingRef.current ||
    advanceInFlightRef.current ||
    hasReachedPracticeTarget(currentCardSessionRepeats, currentCardTargetRepeats);
  const isCurrentCardCompleted =
    PracticeSettingsService.isCardCompleted(currentCard) ||
    (boardBreakPopup?.cardId && boardBreakPopup.cardId === currentCardId);
  const currentVisitToken = currentCardDisplayTokenRef.current;
  const hasContextSequence = !isReadingCard && Boolean(
    currentPracticeCard?.contextPre || currentPracticeCard?.contextAfter
  );
  const cardContextSequenceStep = currentPracticeCard?.contextPre ? 2 : 1;
  const afterContextSequenceStep = cardContextSequenceStep + 1;
  const messageButtonAvailable =
    !isRepeatModeCard &&
    !isTtsInteractionLocked &&
    !isCompletedReviewMode &&
    !isHistoryReviewMode &&
    !hasActiveCelebrationPopup &&
    !hasActiveNpcEncounter &&
    !achievementOverlay &&
    messageButtonCardId === currentCardId;
  const showCurrentCardHintMessages =
    !isRepeatModeCard &&
    currentCardHintMessages.length > 0;
  const quickTestRemainingSeconds = Math.ceil(quickTestRemainingMs / 1000);
  const quickTestCaptionStatus = quickTestPassed
    ? 'passed'
    : quickTestTimeLimitExpired
      ? 'expired'
      : 'active';
  const quickTestFlashCardStatus = {
    visible: quickTestEligible && !isQuickMode && !isRepeatModeCard && !isCompletedReviewMode && !isHistoryReviewMode,
    status: quickTestCaptionStatus,
    label: PracticeSettingsService.getAssessmentGrade(currentCard) === 3
      ? t('practicePhases.memoryTrial.speedLabel')
      : t('practicePhases.memoryTrial.label'),
    value: quickTestPassed
      ? 'Passed'
      : quickTestTimeLimitExpired
        ? 'Time out'
        : `${quickTestRemainingSeconds}s`,
  };
  const quickTestControlsLocked = quickTestFlashCardStatus.visible && quickTestCaptionStatus === 'active';
  const repeatedCurrentCard = currentCardSessionRepeats > 0;
  const verticalNavigationLocked = quickTestControlsLocked || repeatedCurrentCard || isTtsInteractionLocked;
  const showBeginnerArrowLabels = playerLevel === 1;
  const keyboardShortcutsEnabled =
    !importPaused && !isProcessing &&
    !isEditModalOpen &&
    !reviewCard && !cardMenuOpen &&
    !isLevelUpModalOpen &&
    !isConfirmDialogOpen &&
    !hasActiveCelebrationPopup &&
    !hasActiveNpcEncounter &&
    !achievementOverlay &&
    !isCurrentCardAdvanceLocked;
  const practiceGuidancePhase = !isQuickMode && !isCompletedReviewMode && !isHistoryReviewMode
    ? (hasAnswerDecision || repeatedCurrentCard ? 'repeat' : 'recall') : null;
  const showPersistentGuidance = Number.isInteger(playerLevel) && playerLevel >= 1 && playerLevel <= 3;

  return (
    <div className={[
      'practice-session',
      isReadingCard ? 'practice-session-reading' : '',
      currentPracticePhase === PracticeSettingsService.PRACTICE_PHASES.MEMORY_TRIAL
        ? 'practice-session-memory-trial'
        : 'practice-session-core-training',
      isFluencyTestFirstTry ? 'practice-session-memory-trial-first-try' : '',
      achievementOverlay || hasActiveNpcEncounter ? 'practice-session-overlay-active' : '',
    ].filter(Boolean).join(' ')}>
      {isProcessing && (
        <div className="practice-loading-overlay">
          <Spinner size="medium" message={t('interface.common.saving')} />
        </div>
      )}

      <CelebrationPopup
        key={`celebration-${activeCelebrationPopup?.id ?? 'none'}`}
        isVisible={hasActiveCelebrationPopup && !achievementOverlay}
        onClose={closeCurrentCelebrationPopup}
        title={activeCelebrationPopup?.title || ''}
        message={activeCelebrationPopup?.message || ''}
        iconSrc={activeCelebrationPopup?.iconSrc || ''}
        iconAlt={activeCelebrationPopup?.iconAlt || ''}
        iconLayout={activeCelebrationPopup?.iconLayout || 'default'}
        presentation={activeCelebrationPopup?.phaseTransition ? 'transparent-dialog' : 'floating'}
        animationSpriteSrc={activeCelebrationPopup?.animationSpriteSrc || ''}
        animationFrameCount={activeCelebrationPopup?.animationFrameCount}
        animationFrameSize={activeCelebrationPopup?.animationFrameSize}
        animationDurationMs={activeCelebrationPopup?.animationDurationMs}
      />
      <TrainingAchievementOverlay
        key={`achievement-${achievementOverlay?.id ?? 'none'}`}
        isVisible={Boolean(achievementOverlay)}
        message={achievementOverlay?.message || ''}
        spriteSrc={achievementOverlay?.spriteSrc || ''}
        frameCount={achievementOverlay?.frameCount || 4}
        frameWidth={achievementOverlay?.frameWidth || TRAINING_ACHIEVEMENT_FRAME_WIDTH}
        frameHeight={achievementOverlay?.frameHeight || TRAINING_ACHIEVEMENT_FRAME_HEIGHT}
        onDone={closeCurrentCelebrationPopup}
        durationMs={TRAINING_ACHIEVEMENT_DURATION_MS}
      />
      {confirmDialog}
      {npcEncounter?.phase === 'beforeScene' && (
        <ScenePlayer
          scene={npcEncounter.beforeScene}
          onContinue={handleNpcEncounterBeforeSceneDone}
          variant="opponent"
          continueLabel="Face the Challenge"
          nextLabel="Next"
          showVs
          meta={<span>NPC Encounter</span>}
          messageTestId="npc-encounter-before-message"
        />
      )}
      {npcEncounter?.phase === 'prompt' && (
        <div className="npc-encounter-choice" role="dialog" aria-modal="true" aria-label={t('practiceUi.encounter.challengeTitle')}>
          <div className="npc-encounter-choice-panel">
            <Swords size={42} strokeWidth={2.1} aria-hidden="true" />
            <h2>{t('practiceUi.encounter.challengeTitle')}</h2>
            <p>{t('practiceUi.encounter.challengeMessage')}</p>
            <div className="npc-encounter-choice-actions">
              <button type="button" className="npc-choice-fight" onClick={handleNpcEncounterFight}>
                {t('practiceUi.encounter.accept')}
              </button>
              <button type="button" className="npc-choice-skip" onClick={handleNpcEncounterSkip}>
                {t('practiceUi.encounter.later')}
              </button>
            </div>
          </div>
        </div>
      )}
      {npcEncounter?.phase === 'battle' && (
        <NpcEncounterOverlay
          coachingScenes={coachingScenes}
          cards={npcEncounter.cards}
          scene={npcEncounter.beforeScene}
          onComplete={handleNpcEncounterComplete}
          onPracticeRepeat={handleNpcEncounterPracticeRepeat}
          onAnswerViewed={handleNpcEncounterAnswerViewed}
          onCardResult={handleNpcEncounterCardResult}
          onLevelUp={handleNpcEncounterLevelUp}
          isPaused={Boolean(reviewCard) || isLevelUpModalOpen || isConfirmDialogOpen}
          onSkip={handleNpcEncounterSkip}
          canUseTts={canUseTts}
          onMembershipRequired={() => showAlert({
            title: t('membership.tts.title'),
            message: t('membership.tts.message'),
            confirmLabel: t('common.ok'),
            tone: 'warning',
          })}
        />
      )}
      {npcEncounter?.phase === 'resultScene' && (
        <ScenePlayer
          scene={npcEncounter.resultScene}
          onContinue={continueAfterNpcEncounter}
          variant="opponent"
          continueLabel="Return to Practice"
          nextLabel="Next"
          showVs
          meta={<span>{npcEncounter.result?.label || 'Encounter Result'} {npcEncounter.result?.scoreDelta ? `(${npcEncounter.result.scoreDelta > 0 ? '+' : ''}${npcEncounter.result.scoreDelta} pt)` : ''}</span>}
          messageTestId="npc-encounter-result-message"
        />
      )}
      {npcEncounter?.phase === 'resultSummary' && (
        <div className="npc-encounter-choice" role="dialog" aria-modal="true" aria-label="NPC encounter result">
          <div className="npc-encounter-choice-panel">
            <Swords size={42} strokeWidth={2.1} aria-hidden="true" />
            <h2>{npcEncounter.result?.label || 'Encounter Complete'}</h2>
            <p>
              Session points {npcEncounter.result?.scoreDelta > 0 ? '+' : ''}
              {npcEncounter.result?.scoreDelta || 0} pt
            </p>
            <div className="npc-encounter-choice-actions">
              <button type="button" className="npc-choice-fight" onClick={continueAfterNpcEncounter}>
                Return to Practice
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <ErrorMessage
          error={error}
          onRetry={error.sessionEndFailed && !isProcessing ? () => performEndSession() : undefined}
          onDismiss={error.sessionEndFailed ? undefined : () => setError(null)}
          type={error?.isNetworkError ? 'network' : 'api'}
        />
      )}

      {hasNoPracticeCard && !hasBufferedPracticeProgress && !error?.sessionEndFailed && (
        <div className="practice-session-empty">
          <p>
            {flashCards?.length
              ? t('practiceUi.completion.setMessage')
              : 'No flash cards available for practice. Add new notes to begin.'}
          </p>
          <button
            onClick={() => {
              if (hasBufferedPracticeProgress) {
                void performEndSession({ endReason: 'queueExhausted' });
                return;
              }
              onSessionComplete?.({ cardsCompleted: 0, evaluations: [], endReason: 'queueExhausted' });
            }}
            disabled={isProcessing || hasActiveCelebrationPopup}
          >
            <DoorOpen size={18} strokeWidth={2.2} aria-hidden="true" />
            <span>{isProcessing ? 'Saving...' : 'OK'}</span>
          </button>
        </div>
      )}

      {!hasNoPracticeCard && (
        <>
      <LearningSessionHeader
        className="practice-learning-header"
        aria-hidden={hasActiveCelebrationPopup || importPaused || undefined}
        title={<h2 className="page-title-fx page-title-practice" aria-label={isQuickMode ? t('quickPractice.title') : practicePhaseTitle}>
          <span className="practice-phase-full">{isQuickMode ? t('quickPractice.title') : practicePhaseTitle}</span>
          <span aria-hidden="true" className={`practice-mode-badge is-${isQuickMode ? 'quick' : isFluencyTestPhase ? 'fluency' : 'core'}`}>
            {t(`practiceUi.modeBadge.${isQuickMode ? 'quick' : isFluencyTestPhase ? 'fluency' : 'core'}`)}
          </span>
          {currentNoteName && <span className="practice-heading-note" title={currentNoteName}>{currentNoteName}</span>}
        </h2>}
        elapsedSeconds={elapsedTime}
        timeLimitSeconds={SESSION_TIME_LIMIT_SECONDS}
        onExit={handleEndSession}
        exitLabel={t('interface.practice.end')}
        exitDisabled={importPaused || isTtsInteractionLocked || isProcessing || Boolean(slideDirection) || hasActiveCelebrationPopup || hasActiveNpcEncounter || Boolean(achievementOverlay)}
        exitTitle={pendingPracticeSaves > 0 ? t('interface.practice.pendingSaves', undefined, { count: pendingPracticeSaves }) : t('interface.practice.end')}
        leftTools={<div className="practice-audio-tools">{(isReadingCard || !isTtsAnswerVisible) && <button
              type="button"
              className={`practice-sound-toggle ${soundEnabled ? 'is-on' : 'is-off'}`}
              onClick={handleTogglePracticeSound}
              disabled={isTtsInteractionLocked}
              aria-pressed={soundEnabled}
              aria-label={soundButtonLabel}
              title={soundButtonTitle}
            >
              {soundEnabled ? (
                <Volume2 size={14} strokeWidth={2.4} aria-hidden="true" />
              ) : (
                <VolumeX size={14} strokeWidth={2.4} aria-hidden="true" />
              )}
            </button>}
            {!hasActiveNpcEncounter && (isReadingCard ? <ReadingAudioControls audio={readingAudio} canUseTts={canUseTts}
              showAutoplayToggle={false}
              onMembershipRequired={() => showAlert({ title: t('membership.tts.title'), message: t('membership.tts.message'), confirmLabel: t('common.ok'), tone: 'warning' })} /> : <CardListeningControl
              cardId={currentCardId}
              ttsText={currentCard?.ttsText}
              isReading={isReadingCard}
              isRevealed={isRevealed}
              isAnswerVisible={isTtsAnswerVisible}
              visitToken={currentVisitToken}
              canUseTts={canUseTts}
              showAutoplayToggle={false}
              autoplayEnabled={soundEnabled}
              onInteractionLockChange={handleTtsInteractionLockChange}
              onPlaybackCompleted={handleTtsPlaybackCompleted}
              onMembershipRequired={() => showAlert({
                title: t('membership.tts.title'),
                message: t('membership.tts.message'),
                confirmLabel: t('common.ok'),
                tone: 'warning',
              })}
            />)}</div>}
        rightTools={isQuickMode && (onImportCard || ['preset', 'market'].includes(settings.quickSource?.type)) ? <LearningImportButton
          imported={importedCardIds.has(currentCardId) || Boolean(isCardImported?.(currentCard))}
          disabled={importPaused || isTtsInteractionLocked || isCurrentCardAdvanceLocked || isReadingSentenceAdvancing || isProcessing || hasActiveCelebrationPopup || hasActiveNpcEncounter}
          onClick={() => {
            if (onImportCard) onImportCard(currentCardRef.current);
            else setImportCard(currentCardRef.current);
          }}
        /> : !isCompletedReviewMode && !isQuickMode && (
              <CardActionsMenu
                key={currentCardId}
                className="practice-card-actions"
                onEdit={handleOpenEditModal}
                onReview={() => setReviewCard(currentCardRef.current)}
                onOpenChange={setCardMenuOpen}
                disabled={isTtsInteractionLocked || isCurrentCardAdvanceLocked || isReadingSentenceAdvancing || quickTestControlsLocked || isProcessing || hasActiveCelebrationPopup || hasActiveNpcEncounter || Boolean(achievementOverlay)}
              />
            )}
      />

      {!isQuickMode && !isCompletedReviewMode && !isReadingCard && !hasActiveNpcEncounter &&
        <LearningCoach scenes={coachingScenes} selectScene={coachingSelector.current} trigger={displayedCoachingTrigger} visitKey={practiceSessionRecordIdRef.current}
          variables={{ currentIndex: displayedNavigationIndex + 1, testIndex: coachingTestIndex }} />}

      {/* Center the whole workout below the header; only the card may shrink. */}
      <div className="practice-session-workout" aria-hidden={importPaused || undefined} inert={importPaused ? '' : undefined}>
      {currentPracticeCard.contextPre && (
        <div
          key={`context-pre-${currentCardId}`}
          className={[
            'practice-card-memo',
            'practice-card-context-pre',
            hasContextSequence ? 'practice-context-sequence-step practice-context-sequence-step-1' : '',
          ].filter(Boolean).join(' ')}
        >
          <span className="practice-card-memo-text">{currentPracticeCard.contextPre}</span>
        </div>
      )}

      <div className={[
        'practice-session-card',
        isReadingCard ? 'practice-session-card-reading' : '',
        hasContextSequence
          ? `practice-context-sequence-card practice-context-sequence-step-${cardContextSequenceStep}`
          : '',
        slideDirection ? `slide-${slideDirection}` : '',
      ].filter(Boolean).join(' ')} key={`practice-card-${currentCardId}`}>
        {isReadingCard ? (
          <ReadingArticlePractice
            readingAudio={readingAudio}
            key={getCardId(currentCard)}
            card={currentPracticeCard}
            resetKey={readingResetKey}
            targetRepeats={currentCardTargetRepeats}
            getTargetRepeatsForChild={getTargetRepeatsForCard}
            onSentenceRepeat={handleReadingSentenceRepeat}
            onSentenceAutoRepeat={handleReadingSentenceAutoRepeat}
            onCardComplete={handleRepeatModeCardComplete}
            onProgressChange={handleReadingProgressChange}
            onActiveSentenceChange={setActiveReadingSentence}
            getSentenceScorePreview={({ sentenceText, repeatCount, itemType, speechSide, completedWithoutFlip }) =>
              calculateReadingPracticeScoreBreakdown({
                sentenceText,
                repeatCount,
                itemType,
                speechSide,
                completedWithoutFlip,
              })
            }
            onSwipeLeft={advanceToNextCard}
            onSwipeRight={goToPrevCard}
            readOnly={isHistoryReviewMode}
            keyboardShortcutsEnabled={keyboardShortcutsEnabled}
            autoRepeatHoldMs={AUTO_REPEAT_HOLD_MS}
            autoRepeatAvailable={canAutoRepeatCurrentCard}
            canUseTts={canUseTts}
            onMembershipRequired={() => showAlert({
              title: t('membership.tts.title'),
              message: t('membership.tts.message'),
              confirmLabel: t('common.ok'),
              tone: 'warning',
            })}
            onBrowserTtsUnavailable={() => showAlert({
              title: t('browserTts.unavailableTitle'),
              message: t('browserTts.unavailableMessage'),
              confirmLabel: t('common.ok'),
              tone: 'warning',
            })}
          />
        ) : (
          <FlashCard
            key={getCardId(currentCard)}
            card={currentPracticeCard}
            isFlipped={isRevealed}
            onFlip={handleFlip}
            sessionScoreGained={currentCardSessionScoreGained}
            scoreBonuses={currentScoreBonusEvent.bonuses}
            scoreBonusEventId={currentScoreBonusEvent.id}
            sessionPracticeCount={currentCardSessionRepeats}
            onSwipeUp={verticalNavigationLocked ? undefined : () => advanceToNextCard({ userInitiated: true })}
            onSwipeDown={!verticalNavigationLocked && (isQuickMode || isCompletedReviewMode) ? goToPrevCard : undefined}
            onReveal={handleReveal}
            onRevealComplete={handleRevealComplete}
            onAnswerViewed={handleAnswerViewed}
            onAnswerOk={handleAnswerOk}
            onAnswerNo={handleAnswerNo}
            answerChoiceAvailable={!isCompletedReviewMode && !isHistoryReviewMode && !answerDecisionMadeRef.current}
            onBadgeClick={handleAutoRepeatConfirm}
            autoRepeatAvailable={canAutoRepeatCurrentCard}
            autoRepeatHoldMs={AUTO_REPEAT_HOLD_MS}
            reverseMode={reverseMode}
            isCompleted={isCurrentCardCompleted}
            noteCoverUrl={currentNoteCover}
            quickTestStatus={quickTestFlashCardStatus}
            hintMessages={currentCardHintMessages}
            showHintMessages={showCurrentCardHintMessages}
            hintMessageSeed={currentVisitToken}
            messageButtonAvailable={messageButtonAvailable}
            onSaveHintMessages={saveHintMessagesForCurrentCard}
            onClearMessageButton={clearMessageButton}
            verticalNavigationDisabled={verticalNavigationLocked}
            hideVerticalNavigation={quickTestControlsLocked || repeatedCurrentCard}
            hidePreviousNavigation={!isQuickMode && !isCompletedReviewMode}
            hideNextNavigation={!isQuickMode && !isCompletedReviewMode}
            showBeginnerArrowLabels={showBeginnerArrowLabels}
            practiceGuidancePhase={practiceGuidancePhase}
            showPersistentGuidance={showPersistentGuidance}
            showPracticePhaseCaption={!isFluencyTestPhase}
            practiceGuidanceEnabled={keyboardShortcutsEnabled}
            interactionDisabled={isTtsInteractionLocked || isCurrentCardAdvanceLocked}
            keyboardShortcutsEnabled={keyboardShortcutsEnabled}
            backPracticeHint={ttsAutoplayEnabled
              ? 'Click to practice'
              : t('practice.listeningSuggestion')}
          />
        )}
        {!isReadingCard && (
          <div
            className="practice-card-progress progress-bar"
            role="progressbar"
            aria-label={t('interface.practice.cardProgress')}
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={Math.round(progress)}
          >
            <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      {currentPracticeCard.contextAfter && (
        <div
          key={`context-after-${currentCardId}`}
          className={[
            'practice-card-memo',
            'practice-card-context-after',
            hasContextSequence
              ? `practice-context-sequence-step practice-context-sequence-step-${afterContextSequenceStep}`
              : '',
          ].filter(Boolean).join(' ')}
        >
          <span className="practice-card-memo-text">{currentPracticeCard.contextAfter}</span>
        </div>
      )}

      {/* Bottom bar: card info ??grade stars ??end session */}
      <div className="practice-session-bottom">
        {isReadingCard && (
          <div
            className="practice-card-progress practice-card-progress-reading progress-bar"
            role="progressbar"
            aria-label={t('interface.practice.cardProgress')}
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={Math.round(progress)}
          >
            <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
          </div>
        )}
        <div className="practice-card-info" data-testid="practice-card-info">
          <span className="practice-card-repeat-progress" title={`${currentCardTypeLabel} repeat progress`}>
            <span aria-hidden="true">↻</span>
            <span>{isRepeatModeCard
              ? `${PracticeSettingsService.getCardPracticeCount(currentCard)}/${PracticeSettingsService.getCardScoreLimit(currentCard)}`
              : `${currentCardSessionRepeats}/${currentCardTargetRepeats}`}</span>
          </span>
          <span aria-hidden="true">·</span>
          <span title={t(isRepeatModeCard ? 'interface.practice.workoutScore' : 'interface.practice.totalScore')}>
            <span aria-hidden="true">★</span>
            <span>{isRepeatModeCard ? totalScoreGained : `${cardScore}/${cardScoreLimit}`}</span>
          </span>
          <span aria-hidden="true">·</span>
          <span title={t('interface.practice.currentGrade')}>
            <GraduationCap size={14} role="img" aria-label={t('learningNavigation.map.gradeShort')} />
            <span>{currentGradeLabel}</span>
          </span>
          {cardLevel > 1 && <>
            <span aria-hidden="true">·</span>
            <span className="practice-card-level" title={t('learningNavigation.common.level', undefined, { level: cardLevel })}>{`Lv.${cardLevel}`}</span>
          </>}
          <span aria-hidden="true">·</span>
          <span>
            <History size={14} strokeWidth={2.2} aria-hidden="true" />
            <span>
              {daysSince === null ? 'Never practiced' : daysSince === 0 ? 'Today' : `${daysSince}d ago`}
            </span>
          </span>
        </div>

        {!isRepeatModeCard && (
          <div className="practice-evaluation-slot">
            <EvaluationPanel
              key={`evaluation-${currentCardId}-${currentVisitToken}`}
              onEvaluate={async (grade) => {
                const confirmed = await confirm({
                  title: t('interface.assessment.confirmTitle', undefined, { grade }),
                  message: t(`interface.assessment.gradeLabels.${grade}`),
                  confirmLabel: t('common.ok'),
                  cancelLabel: t('common.cancel'),
                });
                if (confirmed && isMountedRef.current) await handleEvaluation(grade);
              }}
              disabled={isTtsInteractionLocked || isCurrentCardAdvanceLocked || isCompletedReviewMode || isHistoryReviewMode || isProcessing || hasActiveCelebrationPopup || hasActiveNpcEncounter || Boolean(achievementOverlay)}
              selectedGrade={selectedRating}
              autoGradeCue={autoGradeCue?.cardId === String(currentCardId) && autoGradeCue?.visitToken === currentVisitToken ? autoGradeCue : null}
              quickGradeLimitExpired={quickTestTimeLimitExpired}
              gradeUpdated={isGradeUpdated}
            />
          </div>
        )}

      </div>

      </div>

      {reviewCard && <ReviewLevelUpDialog card={reviewCard} onClose={() => setReviewCard(null)} />}
      {importCard && <ReviewLevelUpDialog card={importCard} importCard onClose={() => setImportCard(null)}
        onCreated={() => setImportedCardIds(previous => new Set(previous).add(getCardId(importCard)))} />}
      {isEditModalOpen && (
        <div
          className="modal-overlay practice-edit-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsEditModalOpen(false);
              setEditingCard(null);
              editingCardRef.current = null;
            }
          }}
        >
          <div
            className="modal-content flashcard-edit-modal-content practice-edit-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Edit Flash Card</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingCard(null);
                  editingCardRef.current = null;
                }}
                aria-label={t('interface.practice.closeEdit')}
              >
                {'\u00D7'}
              </button>
            </div>
            <FlashCardForm
              initialData={editingCard || currentCard}
              onSubmit={handleEditSubmit}
              onCancel={() => {
                setIsEditModalOpen(false);
                setEditingCard(null);
                editingCardRef.current = null;
              }}
              onDelete={handleDeleteCurrentCard}
              showCancelAction={false}
            />
          </div>
        </div>
      )}

      {isLevelUpModalOpen && (
        <div
          className="modal-overlay practice-edit-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('interface.map.levelUp')}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsLevelUpModalOpen(false);
              setEditingCard(null);
              editingCardRef.current = null;
            }
          }}
        >
          <div
            className="modal-content flashcard-edit-modal-content practice-edit-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>Edit Flash Card</h2>
                <p className="level-up-modal-level-caption">
                  Level {PracticeSettingsService.getCardLevel(editingCard || currentCard)} to Level {Math.min(8, PracticeSettingsService.getCardLevel(editingCard || currentCard) + 1)}
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setIsLevelUpModalOpen(false);
                  setEditingCard(null);
                  editingCardRef.current = null;
                }}
                aria-label={t('interface.practice.closeLevelUp')}
              >
                {'\u00D7'}
              </button>
            </div>
            <LevelUpFlashCardForm
              initialData={editingCard || currentCard}
              onLevelUp={handleEditLevelUp}
              onCancel={() => {
                setIsLevelUpModalOpen(false);
                setEditingCard(null);
                editingCardRef.current = null;
              }}
            />
          </div>
        </div>
      )}
        </>
      )}

    </div>
  );
}

export default PracticeSession;
